import { useWorkspaceAgentSelection } from "../hooks/useWorkspaceAgentSelection";
import {
  sendWithWorkspaceOnboardingHeaders,
  shouldSendWorkspaceOnboardingHeaders,
  WORKSPACE_ONBOARDING_TRIGGER_MESSAGE,
} from "../utils/workspaceOnboarding";
import { reconcileToolChips } from "../utils/toolChipReconciliation";
import { prepareChatMessagesForDisplay } from "../utils/prepareChatMessagesForDisplay";
import { loadCreatedGptLiveConversationAndRefreshInBackground } from "../utils/gptLiveConversation";
// src/modules/ida/views/Chat.tsx
//
// What? Main chat view for IDA (Intelligent Document Agent).
// Why? To provide an agentic chat interface where the LLM decides retrieval strategy.
// How? Uses @metalmind-ab/mmagent-chat-ui for chat functionality with automatic citation handling.

import { settleCancelledToolExecutions } from "../utils/cancelledToolExecutions";
import { isConfirmedUserStop } from "../utils/userStop";
import React, {
  useState,
  useEffect,
  useCallback,
  useReducer,
  useRef,
  useMemo,
} from "react";
import { Box, CircularProgress, Alert, Button, IconButton } from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import {
  ChatProvider,
  useChat,
  useLoadConversation,
  useChatStore,
  useActiveSubmissionStop,
} from "@metalmind-ab/mmagent-chat-ui";
import type {
  ChatMessageType,
  Citation,
  ImageReference,
  ChatRequestErrorContext,
} from "@metalmind-ab/mmagent-chat-ui";
import "@metalmind-ab/mmagent-chat-ui/styles.css";
import "../styles/chat-theme.css";

import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { useFeatureFlag } from "../../../providers/FeatureFlagProvider";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import {
  createDocumentCardCompletionReporter,
  createFrontendErrorReporter,
  createFrontendRequestId,
  createFrontendStreamTroubleReporter,
  reportPackageChatError,
  type FrontendRequestFailureContext,
} from "../../../services/frontendErrorReporting";
import {
  buildIdaChatStreamConfig,
  clearConversationStreamTurn,
  isIdaStreamRetryExhaustion,
  reportIdaStreamTrouble,
  hashDiagnosticId,
  notifyConversationStreamRecovery,
  subscribeToConversationSseActivity,
  subscribeToConversationSseFrames,
} from "../utils/idaChatStreamConfig";
import { buildChatResumeOptions } from "../../../utils/chatResumeOptions";
import { appendStreamInterruptedMarkerOnce } from "../../../utils/streamInterruptedMarker";
import { withServerStop } from "../utils/serverStopConfig";
import { useChatImageAttachments } from "../../../hooks/useChatImageAttachments";
import { useReadUser, useWorkspaceUserList } from "../../../services/api";
import {
  useSendMessageWithFiles,
  type ChatMessageWithFiles,
} from "../hooks/useSendMessageWithFiles";
import {
  registerBackgroundJobForResilience,
  useBackgroundJobProgressResilience,
} from "../hooks/useBackgroundJobProgressResilience";
import { useToolComposingFeedback } from "../hooks/useToolComposingFeedback";
import { useAgentStatusPolling } from "../hooks/useAgentStatusPolling";
import {
  useSilentStreamRecovery,
  coalesceForcedMessagePoll,
  fetchWithAbortTimeout,
  isFinalConversationSummary,
  STREAM_RECONCILE_REQUEST_TIMEOUT_MS,
  type SilentStreamReconcileResult,
} from "../hooks/useSilentStreamRecovery";
import { useAgentStatusStream } from "../hooks/useAgentStatusStream";
import { useHeartbeatCompletionSignal } from "../hooks/useHeartbeatCompletionSignal";
import { useJobCompletionRefresh } from "../hooks/useJobCompletionRefresh";
import { useOnboardingExitRefresh } from "../hooks/useOnboardingExitRefresh";
import { useWorkspaceOnboardingAutoTrigger } from "../hooks/useWorkspaceOnboardingAutoTrigger";
import { useWorkspaceOnboardingChatSelection } from "../hooks/useWorkspaceOnboardingChatSelection";
import { useGptLiveTranscriptMessages } from "../hooks/useGptLiveTranscriptMessages";
import { useConversationQueue } from "../hooks/useConversationQueue";
import { useChatProviderGeneration } from "../hooks/useChatProviderGeneration";
import { createFreshChatConversationGuard } from "../utils/freshChatConversationGuard";
import { cancelLocalSendForFinalStatus } from "../utils/cancelLocalSendForFinalStatus";
import { FullPageChatLayout } from "../components/FullPageChatLayout";
import {
  GptLiveVoiceControl,
  type GptLiveVoiceFeedback,
} from "../components/gptLiveVoice/GptLiveVoiceControl";
import type {
  GptLiveTranscriptBoundary,
  GptLiveTranscriptTurn,
  GptLiveVoicePhase,
} from "../services/gptLiveVoiceTransport";
import { DocumentPreview } from "../components/DocumentPreview";
import { HeartbeatPanel } from "../components/HeartbeatPanel";
import { useQuotaStatus } from "../../../services/api";
import { getChatVoiceFeatures } from "./chatVoiceFeatures";
import {
  useDownloadDocument,
  useFollowUpSuggestions,
  useStarterSuggestions,
} from "../services/api";
import { useFollowUpSuggestionChips } from "../hooks/useFollowUpSuggestionChips";
import { useStarterSuggestionChips } from "../hooks/useStarterSuggestionChips";
import { type IDAChatMessage } from "../utils/chatMessageMapper";
import {
  backfillConversationMessageWindowUntilVisible,
  buildConversationMessagePageUrl,
  mapConversationMessageWindow,
  MAX_MESSAGE_GAP_RECONNECT_PAGES,
  mergeNewestConversationMessagePage,
  parseConversationMessagePage,
  prependOlderConversationMessagePage,
  reconnectConversationMessageGap,
  removeConversationMessagesFrom,
  replaceConversationMessageWindow,
  type ConversationMessageWindow,
} from "../utils/conversationMessageWindow";
import { resolveDeleteHubMessageId } from "../utils/deleteMessageResolution";
import { upsertActiveConversationJobs } from "../utils/activeConversationJobs";
import {
  mergeConversationHistoryWithLiveMessages,
  mergeConversationMessages,
  preserveLiveDocumentExecutions,
} from "../utils/conversationPolling";
import {
  handleRestoreFailure,
  isConversationNotFound,
} from "../utils/conversationRestore";
import { recoverAgentDeleteFailure } from "../utils/agentDeleteRecovery";
import { getOptimisticCurrentUserMetadata } from "../utils/optimisticMessageProvenance";
import {
  applyVoiceAssistantPlaybackState,
  mergeVoiceTranscriptMessages,
  refreshVoiceTranscriptAtBoundary,
} from "../utils/voiceTranscriptMessages";
import {
  reduceHistoryLoadState,
  initialHistoryLoadState,
} from "../utils/historyLoadState";
import {
  STREAM_CHECK_INTERVAL_MS,
  computeStreamActivitySignature,
  isStreamStuck,
} from "../utils/streamStuckDetector";
import type { SkillInvocationAttributionResponse } from "../../../common/types";
import type { User, WorkspaceUser } from "../../../common/types";
import { getApiBaseUrl } from "../../../config/runtimeConfig";
import {
  type CollaborationConversationListResponse,
  type CollaborationConversationSummary,
  type ConversationCollaborator,
  useAddConversationCollaborators,
  useChatConversationSummaries,
  useConversationCollaborators,
  useDeleteChatConversation,
  useRemoveConversationCollaborator,
  useRenameChatConversation,
  useUpdateAgentConversation,
  useAssignAgentAvatar,
  useAgentAvatarReservations,
  useReleaseAgentAvatar,
  useCreateEmptyChatConversation,
} from "../services/collaboration";
import {
  applyAgentStatusStreamEvent,
  mergeAgentPersonaPatchResponse,
  mergeAgentStatusSnapshot,
  type AgentStatusStreamEvent,
} from "../utils/agentStatusStream";
import {
  useTutorialTarget,
  useTutorialHint,
} from "../../../components/Tutorial";

export const AGENT_ONBOARDING_TRIGGER_MESSAGE =
  "You are this workspace's new agent. First, quickly explore this workspace and summarize what you can do here. Then ask me what name, role, and persona I want you to take for ongoing work. Once I answer, apply the name and persona to yourself with set_agent_name and set_agent_persona. Do not spawn another agent.";

const AGENT_INVENTORY_LIMIT = 100;

interface PendingComposerDraft {
  draft: string;
  restore: () => void;
  submissionId: string | null;
  streamStarted: boolean;
  sending: boolean;
  restored: boolean;
  assistantMessageId: string | null;
}

/** UI configuration returned from the backend for workspace personalization */
interface WorkspaceUIConfig {
  workspace_id: string;
  welcome_description: string | null;
  input_placeholder: string | null;
  icon_suggestions: string[];
  has_documents: boolean;
  // Onboarding fields
  needs_onboarding: boolean;
  workspace_name: string | null;
  is_admin: boolean;
}

// Keep the selected conversation in the chat store while its history is read.
// The caller is the tile-click path; keeping this operation here makes its
// ordering independently testable.
export async function requestConversationPageForSelection<T>(
  targetConversationId: number,
  setActiveConversationId: (conversationId: number) => void,
  waitForConversationSubscription: (conversationId: number) => Promise<void>,
  fetchConversationMessagePage: (conversationId: number) => Promise<T>,
): Promise<T> {
  setActiveConversationId(targetConversationId);
  await waitForConversationSubscription(targetConversationId);
  return fetchConversationMessagePage(targetConversationId);
}

export function useAgentCreationTimer() {
  const [agentCreationPending, setAgentCreationPending] = useState(false);
  const agentCreationPendingRef = useRef(false);
  const agentCreationTimerRef = useRef<number | null>(null);

  const cancelPendingAgentCreation = useCallback(() => {
    if (agentCreationTimerRef.current !== null) {
      window.clearTimeout(agentCreationTimerRef.current);
      agentCreationTimerRef.current = null;
    }
    agentCreationPendingRef.current = false;
    setAgentCreationPending(false);
  }, []);

  useEffect(() => {
    return () => {
      if (agentCreationTimerRef.current !== null) {
        window.clearTimeout(agentCreationTimerRef.current);
        agentCreationTimerRef.current = null;
      }
      agentCreationPendingRef.current = false;
    };
  }, []);

  const scheduleAgentCreation = useCallback(
    (
      sendOnboarding: () => Promise<unknown>,
      onSendError: (error: unknown) => void,
    ): boolean => {
      // The ref changes before React can render disabled controls, so every
      // caller of the shared callback is protected from a fast second click.
      if (agentCreationPendingRef.current) return false;
      agentCreationPendingRef.current = true;
      setAgentCreationPending(true);
      agentCreationTimerRef.current = window.setTimeout(() => {
        agentCreationTimerRef.current = null;
        try {
          void sendOnboarding().catch(onSendError);
        } finally {
          agentCreationPendingRef.current = false;
          setAgentCreationPending(false);
        }
      }, 250);
      return true;
    },
    [],
  );

  return {
    agentCreationPending,
    cancelPendingAgentCreation,
    scheduleAgentCreation,
  };
}

/**
 * Main IDA Chat component.
 * Wraps the chat UI with authentication and workspace context.
 */
const Chat: React.FC = () => {
  const { workspace } = useWorkspaceContext();
  const authFetch = useAuthFetch("json");
  const reportFrontendError = useMemo(
    () => createFrontendErrorReporter(authFetch),
    [authFetch],
  );
  const reportFrontendStreamTrouble = useMemo(
    () => createFrontendStreamTroubleReporter(authFetch),
    [authFetch],
  );
  const [authHeaders, setAuthHeaders] = useState<Record<string, string> | null>(
    null,
  );
  const [authError, setAuthError] = useState<string | null>(null);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(
    null,
  );
  const {
    providerKey: chatProviderKey,
    reset: resetChatProvider,
    freshChatConversationGuardRef,
  } = useChatProviderGeneration(currentWorkspaceId);
  const handlePackageConversationUpdated = useCallback(
    (candidate: string | number) => {
      if (
        freshChatConversationGuardRef.current.acceptResponseIdentity(candidate)
      ) {
        return;
      }
      const currentId = useChatStore.getState().conversationId;
      if (currentId !== null && String(currentId) === String(candidate)) {
        useChatStore.getState().setConversationId(null);
      }
    },
    [freshChatConversationGuardRef],
  );
  const [onboardingConfig, setOnboardingConfig] = useState<{
    needs_onboarding: boolean;
    onboarding_agent_exists: boolean;
    onboarding_agent_id: number | null;
    workspace_name: string | null;
    is_admin: boolean;
  } | null>(null);
  const getAuthHeaders = useAuthHeaders();
  const submissionIdListenerRef = useRef<
    ((submissionId: string, conversationId: string) => void) | null
  >(null);
  const pendingComposerDraftRef = useRef<PendingComposerDraft | null>(null);

  // Get store actions to reset state when workspace changes
  const clearMessages = useChatStore((state) => state.clearMessages);
  const setConversationId = useChatStore((state) => state.setConversationId);
  const setConversations = useChatStore((state) => state.setConversations);
  // Handle workspace changes - clear store and force remount
  useEffect(() => {
    if (workspace?.id && workspace.id !== currentWorkspaceId) {
      // Clear the store
      clearMessages();
      setConversationId(null);
      setConversations([], 0);
      setOnboardingConfig(null); // Reset onboarding state
      // Briefly set to null to force unmount, then set new ID
      setCurrentWorkspaceId(null);
      // Use setTimeout to ensure unmount happens before remount
      const timer = setTimeout(() => {
        setCurrentWorkspaceId(workspace.id);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [
    workspace?.id,
    currentWorkspaceId,
    clearMessages,
    setConversationId,
    setConversations,
  ]);

  // Refresh auth headers helper
  const refreshAuthHeaders = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      setAuthHeaders(headers);
    } catch (err) {
      setAuthError((err as Error).message);
    }
  }, [getAuthHeaders]);

  // Fetch auth headers on mount
  useEffect(() => {
    refreshAuthHeaders();
  }, [refreshAuthHeaders]);

  // Refresh auth headers periodically and when tab becomes visible again
  useEffect(() => {
    // Refresh every 4 minutes to stay ahead of token expiry
    const interval = setInterval(refreshAuthHeaders, 4 * 60 * 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshAuthHeaders();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshAuthHeaders]);

  // Fetch onboarding config early to include headers in ChatProvider.
  // Re-fetch when workspace changes (not on token refresh), AND explicitly
  // after onboarding completes (via refreshOnboardingConfig passed to
  // ChatContent). Task #47: needs_onboarding is computed server-side
  // (description/assistant_instructions empty) and the BE clears it as soon as
  // workspace settings persist; without an explicit refetch here the FE keeps
  // sending stale X-Onboarding-Mode until a remount, holding the admin on the
  // onboarding code path long after onboarding is actually done.
  const hasAuth = authHeaders !== null;
  const refreshOnboardingConfig = useCallback(async () => {
    if (!hasAuth || !currentWorkspaceId) return;
    try {
      const config = await authFetch(
        `/api/workspace/ida/context/ui-config`,
        "fetch onboarding config",
        { headers: { "X-Workspace-Id": currentWorkspaceId } },
      );
      setOnboardingConfig({
        needs_onboarding: config.needs_onboarding || false,
        onboarding_agent_exists: config.onboarding_agent_exists || false,
        onboarding_agent_id:
          typeof config.onboarding_agent_id === "number"
            ? config.onboarding_agent_id
            : null,
        workspace_name: config.workspace_name,
        is_admin: config.is_admin || false,
      });
    } catch (err) {
      console.warn("Failed to fetch onboarding config:", err);
      setOnboardingConfig({
        needs_onboarding: false,
        onboarding_agent_exists: false,
        onboarding_agent_id: null,
        workspace_name: null,
        is_admin: false,
      });
    }
  }, [hasAuth, currentWorkspaceId, authFetch]);
  useEffect(() => {
    void refreshOnboardingConfig();
  }, [refreshOnboardingConfig]);

  // Configure chat UI - apiBaseUrl should NOT include /chat (library appends it)
  const apiBaseUrl = `${getApiBaseUrl()}/api/workspace/ida`;
  const imageAttachments = useChatImageAttachments(
    apiBaseUrl,
    currentWorkspaceId ?? "",
    getAuthHeaders,
  );

  // Memoize headers and config to prevent unnecessary ChatProvider re-renders.
  // These hooks must be called before any early returns.
  const needsOnboarding =
    onboardingConfig?.needs_onboarding && onboardingConfig?.is_admin;
  const onboardingAgentExists =
    onboardingConfig?.onboarding_agent_exists ?? false;
  const onboardingAgentId = onboardingConfig?.onboarding_agent_id ?? null;
  const onboardingWorkspaceName = onboardingConfig?.workspace_name;
  const workspaceMode =
    workspace?.features?.mode === "agent" ? "agent" : "chat";

  const headersWithWorkspace = useMemo<Record<string, string>>(() => {
    if (!authHeaders || !currentWorkspaceId) return {};
    return {
      ...authHeaders,
      "X-Workspace-Id": currentWorkspaceId,
    };
  }, [authHeaders, currentWorkspaceId]);

  const chatConfig = useMemo(
    () => ({
      apiBaseUrl,
      headers: headersWithWorkspace,
      imageAttachmentsEnabled: imageAttachments.enabled,
      // The app-level resilience hook is the single per-job stream consumer.
      // ChatProvider's useJobStream reads from the same backend queue and would
      // otherwise race it for progress and terminal events.
      shouldSubscribeToJob: () => false,
      onJobStarted: registerBackgroundJobForResilience,
      uploadImage: (file: File) =>
        imageAttachments.uploadImage(
          file,
          useChatStore.getState().conversationId,
        ),
      // Task #580 Phase 3b: SSE resume against BE Last-Event-ID broker
      // (mmagent-chat-ui 0.1.75 plumbs through to internal useChatStream).
      ...buildIdaChatStreamConfig(apiBaseUrl, {
        workspaceId: currentWorkspaceId ?? undefined,
        reportStreamTrouble: reportFrontendStreamTrouble,
      }),
      onSubmissionId: (submissionId: string, conversationId: string) => {
        const pendingDraft = pendingComposerDraftRef.current;
        if (pendingDraft?.sending) {
          pendingDraft.submissionId = submissionId;
        }
        submissionIdListenerRef.current?.(submissionId, conversationId);
      },
      onError: (error: Error, context?: ChatRequestErrorContext) => {
        reportPackageChatError(reportFrontendError, error, context);
        if (isIdaStreamRetryExhaustion(error)) {
          reportIdaStreamTrouble(
            "stream_retries_exhausted",
            3,
            currentWorkspaceId ?? undefined,
            reportFrontendStreamTrouble,
          );
        }
        clearConversationStreamTurn(useChatStore.getState().conversationId);
        const pendingDraft = pendingComposerDraftRef.current;
        if (error.name === "ServerStreamError" && pendingDraft?.sending) {
          pendingDraft.streamStarted = true;
        }
        const assistantMessage = pendingDraft?.assistantMessageId
          ? useChatStore
              .getState()
              .messages.find(
                (message) => message.id === pendingDraft.assistantMessageId,
              )
          : undefined;
        if (pendingDraft && assistantMessage?.metadata?.assistant_turn_id) {
          pendingDraft.streamStarted = true;
        }
        if (
          pendingDraft?.sending &&
          !pendingDraft.streamStarted &&
          !pendingDraft.restored
        ) {
          pendingDraft.restored = true;
          pendingDraft.restore();
        }
        if (pendingDraft?.sending) pendingComposerDraftRef.current = null;
        notifyConversationStreamRecovery();
      },
      onTerminalAccepted: (terminal: { submissionId: string }) => {
        const pendingDraft = pendingComposerDraftRef.current;
        if (
          pendingDraft?.sending &&
          pendingDraft.submissionId === terminal.submissionId
        ) {
          pendingComposerDraftRef.current = null;
        }
      },
      onConversationUpdated: handlePackageConversationUpdated,
    }),
    [
      apiBaseUrl,
      headersWithWorkspace,
      imageAttachments,
      handlePackageConversationUpdated,
      currentWorkspaceId,
      reportFrontendError,
      reportFrontendStreamTrouble,
    ],
  );

  // Show loading while fetching auth headers
  if (authHeaders === null && !authError) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error if auth failed
  if (authError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert color="danger">Authentication failed: {authError}</Alert>
      </Box>
    );
  }

  // Check workspace
  if (!workspace?.id) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert color="warning">No workspace selected</Alert>
      </Box>
    );
  }

  // Wait for workspace transition to complete (currentWorkspaceId briefly becomes null)
  // Also wait for onboarding config to be loaded
  if (!currentWorkspaceId || onboardingConfig === null) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ChatProvider key={chatProviderKey} config={chatConfig}>
      <ChatContent
        workspaceId={currentWorkspaceId}
        workspaceName={workspace.name}
        workspaceMode={workspaceMode}
        authHeaders={headersWithWorkspace}
        getAuthHeaders={getAuthHeaders}
        imageAttachments={imageAttachments}
        needsOnboarding={needsOnboarding || false}
        onboardingAgentExists={onboardingAgentExists}
        onboardingAgentId={onboardingAgentId}
        onboardingWorkspaceName={onboardingWorkspaceName ?? undefined}
        isAdmin={onboardingConfig?.is_admin || false}
        refreshOnboardingConfig={refreshOnboardingConfig}
        submissionIdListenerRef={submissionIdListenerRef}
        pendingComposerDraftRef={pendingComposerDraftRef}
        apiBaseUrl={apiBaseUrl}
        resetChatProvider={resetChatProvider}
        freshChatConversationGuardRef={freshChatConversationGuardRef}
      />
    </ChatProvider>
  );
};

/**
 * Inner chat content component that uses mmagent-chat-ui hooks.
 */
interface ChatContentProps {
  workspaceId: string;
  workspaceName: string;
  workspaceMode: "chat" | "agent";
  authHeaders: Record<string, string>;
  getAuthHeaders: () => Promise<Record<string, string>>;
  imageAttachments: ReturnType<typeof useChatImageAttachments>;
  needsOnboarding: boolean;
  onboardingAgentExists: boolean;
  onboardingAgentId: number | null;
  onboardingWorkspaceName?: string;
  isAdmin: boolean;
  refreshOnboardingConfig: () => Promise<void>;
  resetChatProvider: ReturnType<typeof useChatProviderGeneration>["reset"];
  freshChatConversationGuardRef: React.MutableRefObject<
    ReturnType<typeof createFreshChatConversationGuard>
  >;
  submissionIdListenerRef: React.MutableRefObject<
    ((submissionId: string, conversationId: string) => void) | null
  >;
  pendingComposerDraftRef: React.MutableRefObject<PendingComposerDraft | null>;
  apiBaseUrl: string;
}

/** Poll interval for foreground conversation refresh (10 seconds) */
const MESSAGE_POLL_INTERVAL_MS = 10 * 1000;

const ChatContent: React.FC<ChatContentProps> = ({
  workspaceId,
  workspaceName,
  workspaceMode,
  authHeaders,
  getAuthHeaders,
  imageAttachments,
  needsOnboarding,
  onboardingAgentExists,
  onboardingAgentId,
  onboardingWorkspaceName,
  isAdmin,
  refreshOnboardingConfig,
  resetChatProvider,
  freshChatConversationGuardRef,
  submissionIdListenerRef,
  pendingComposerDraftRef,
  apiBaseUrl,
}) => {
  const { refreshWorkspace, workspace } = useWorkspaceContext();
  const { periodicExceeded } = useQuotaStatus();
  const authFetch = useAuthFetch("json");
  const reportFrontendError = useMemo(
    () => createFrontendErrorReporter(authFetch),
    [authFetch],
  );
  const reportChatHistoryFailure = useCallback(
    (
      operation: "conversation_list" | "conversation_messages",
      context: FrontendRequestFailureContext,
    ) => {
      const message =
        operation === "conversation_list"
          ? "IDA conversation list request failed"
          : "IDA conversation history request failed";
      try {
        reportFrontendError(new Error(message), "", "chat_history", context);
      } catch {
        // A synchronous diagnostic failure must not replace the request error.
      }
    },
    [reportFrontendError],
  );
  const reportConversationListFailure = useCallback(
    (context: FrontendRequestFailureContext) =>
      reportChatHistoryFailure("conversation_list", context),
    [reportChatHistoryFailure],
  );
  const reportDocumentCardCompletionDiagnostic = useMemo(
    () => createDocumentCardCompletionReporter(authFetch),
    [authFetch],
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedCitation, setSelectedCitation] = useState<Citation | null>(
    null,
  );
  const [uiConfig, setUiConfig] = useState<WorkspaceUIConfig | null>(null);
  const [uiConfigLoaded, setUiConfigLoaded] = useState(false);
  const {
    agentCreationPending,
    cancelPendingAgentCreation,
    scheduleAgentCreation,
  } = useAgentCreationTimer();
  const [skillAttributionsByTurnId, setSkillAttributionsByTurnId] = useState<
    Record<number, SkillInvocationAttributionResponse["items"]>
  >({});
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [workspaceUsers, setWorkspaceUsers] = useState<WorkspaceUser[]>([]);
  const [conversations, setConversations] = useState<
    CollaborationConversationSummary[]
  >([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [
    conversationInventoryWorkspaceId,
    setConversationInventoryWorkspaceId,
  ] = useState<string | null>(null);
  const [
    onboardingChatAutoSelectionSuppressedWorkspaceId,
    setOnboardingChatAutoSelectionSuppressedWorkspaceId,
  ] = useState<string | null>(null);
  const loadMoreConversationsInFlightRef = useRef(false);
  const [conversationSearch, setConversationSearch] = useState("");
  const [newAgentSelectionPending, setNewAgentSelectionPending] =
    useState(false);
  const [conversationTotal, setConversationTotal] = useState(0);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [olderMessagesLoading, setOlderMessagesLoading] = useState(false);
  // True when the most recent "Load earlier messages" attempt failed. The
  // currently visible window and cursor are left untouched so this only
  // adds a visible signal beside the retry control.
  const [olderMessagesLoadFailed, setOlderMessagesLoadFailed] = useState(false);
  // Set when the background message-sync poll hits a non-retryable error
  // (413: a stored row exceeds the Hub's page byte ceiling and no retry can
  // succeed). Distinct from historyLoadFailed: the conversation's already-
  // loaded messages stay visible; only the background sync is affected.
  const [messagesSyncError, setMessagesSyncError] = useState<string | null>(
    null,
  );
  const [deleteMessageError, setDeleteMessageError] = useState<string | null>(
    null,
  );
  const [streamRecoverySignal, setStreamRecoverySignal] = useState(0);
  const streamReconcileInFlightRef = useRef(false);
  const [conversationCollaboratorsById, setConversationCollaboratorsById] =
    useState<Record<number, ConversationCollaborator[]>>({});
  const [
    currentConversationCollaborators,
    setCurrentConversationCollaborators,
  ] = useState<ConversationCollaborator[]>([]);
  const [
    conversationCollaboratorsLoading,
    setConversationCollaboratorsLoading,
  ] = useState(false);
  const [conversationCollaboratorsError, setConversationCollaboratorsError] =
    useState<string | null>(null);
  const onboardingInProgressRef = useRef(false);
  const prevUiNeedsOnboardingRef = useRef<boolean | null>(null);
  const collaborationEnabled = useFeatureFlag("collaboration_chat_enabled");
  const skillAttributionEnabled = useFeatureFlag(
    "skill_invocation_attribution",
  );
  const voiceInputEnabled = useFeatureFlag("voice_input_enabled");
  const gptLiveEnabled = useFeatureFlag("ida_gpt_live_enabled");
  const chatVoiceFeatures = getChatVoiceFeatures(
    gptLiveEnabled,
    voiceInputEnabled,
  );
  const followUpSuggestionsEnabled = useFeatureFlag(
    "ida_follow_up_suggestions",
  );
  const fetchFollowUpSuggestions = useFollowUpSuggestions();
  const fetchStarterSuggestions = useStarterSuggestions();
  const readUser = useReadUser();
  const listWorkspaceUsers = useWorkspaceUserList();
  const listConversationSummaries = useChatConversationSummaries(
    reportConversationListFailure,
  );
  const createEmptyChatConversation = useCreateEmptyChatConversation();
  const listConversationCollaborators = useConversationCollaborators();
  const addConversationCollaborators = useAddConversationCollaborators();
  const removeConversationCollaborator = useRemoveConversationCollaborator();
  const deleteChatConversation = useDeleteChatConversation();
  const renameChatConversation = useRenameChatConversation();
  const updateAgentConversation = useUpdateAgentConversation();
  const assignAgentAvatar = useAssignAgentAvatar();
  const getAgentAvatarReservations = useAgentAvatarReservations();
  const releaseAgentAvatar = useReleaseAgentAvatar();
  const downloadDocument = useDownloadDocument();

  const composerAnchorRef = useTutorialTarget("ida-onboarding-composer");

  // mmagent-chat-ui hooks
  const { messages, sendMessage, isLoading, isSending, cancelStream } =
    useChat();
  const conversationId = useChatStore((state) => state.conversationId);
  useEffect(() => {
    setDeleteMessageError(null);
  }, [conversationId]);
  const {
    label: toolComposingLabel,
    startVoiceComposition,
    clearVoiceCompositions,
  } = useToolComposingFeedback(conversationId);
  const handleVoiceToolComposing = useCallback(
    (callId: string | null) => {
      if (callId) {
        startVoiceComposition(callId);
      } else {
        clearVoiceCompositions();
      }
    },
    [clearVoiceCompositions, startVoiceComposition],
  );
  const conversationQueue = useConversationQueue({
    apiBaseUrl,
    workspaceId,
    conversationId,
    getAuthHeaders,
  });
  useEffect(() => {
    const unsubscribeQueue = subscribeToConversationSseFrames(
      conversationQueue.handleFrame,
    );
    const unsubscribeDraft = subscribeToConversationSseFrames((frame) => {
      const pendingDraft = pendingComposerDraftRef.current;
      if (
        pendingDraft?.sending &&
        frame.source === "post" &&
        pendingDraft.submissionId !== null &&
        frame.submission_id === pendingDraft.submissionId
      ) {
        pendingDraft.streamStarted = true;
        pendingComposerDraftRef.current = null;
      }
    });
    return () => {
      unsubscribeQueue();
      unsubscribeDraft();
    };
  }, [conversationQueue.handleFrame, pendingComposerDraftRef]);
  const initialConversationIdRef = useRef(conversationId);
  const initialConversationHydrationStartedRef = useRef(false);
  useEffect(() => {
    if (conversationId !== null && newAgentSelectionPending) {
      setNewAgentSelectionPending(false);
    }
  }, [conversationId, newAgentSelectionPending]);
  const subscriptionReadyConversationIdRef = useRef<string | null>(null);
  const subscriptionStartWaitersRef = useRef<Array<() => void>>([]);

  // useChat's passive conversation subscription effect is registered inside
  // the hook above. This following effect runs after it has opened (or
  // attempted to open) the selected conversation stream, so history loading
  // cannot outrun the replaying GET on a late join.
  useEffect(() => {
    if (conversationId === null) return;
    subscriptionReadyConversationIdRef.current = String(conversationId);
    const waiters = subscriptionStartWaitersRef.current;
    subscriptionStartWaitersRef.current = [];
    waiters.forEach((resolve) => resolve());
  }, [conversationId]);

  const waitForConversationSubscription = useCallback(
    (targetConversationId: number) => {
      const targetId = String(targetConversationId);
      // An active send owns the stream and chat-ui suppresses the passive
      // subscription in that state. Do not hold the history request behind it.
      if (
        isLoading ||
        subscriptionReadyConversationIdRef.current === targetId
      ) {
        return Promise.resolve();
      }
      return new Promise<void>((resolve) => {
        subscriptionStartWaitersRef.current.push(resolve);
      });
    },
    [isLoading],
  );
  const setConversationForSelection = useCallback(
    (targetConversationId: number) => {
      freshChatConversationGuardRef.current.clearForConversationSelection(
        targetConversationId,
      );
      if (
        useChatStore.getState().conversationId !== String(targetConversationId)
      ) {
        subscriptionReadyConversationIdRef.current = null;
      }
      useChatStore.getState().setConversationId(String(targetConversationId));
    },
    [freshChatConversationGuardRef],
  );
  const [userStoppedConversationId, setUserStoppedConversationId] = useState<
    string | null
  >(null);
  const stopConversationIdRef = useRef<string | null>(null);

  // Track when loading started + the last observed stream-activity signature
  // for stuck detection. The signature (#126) folds in every stream-event
  // source so a content-silent tool batch still counts as activity.
  const loadingStartTimeRef = useRef<number | null>(null);
  const lastActivitySignatureRef = useRef<string>("");
  const lastActivityTimeRef = useRef<number>(Date.now());
  const recordStreamActivityRef = useRef<() => void>(() => {});
  const wasAgentTurnActiveRef = useRef(false);
  const setLoading = useChatStore((state) => state.setLoading);
  // Shared with the upload and replay-error handlers so overlapping recovery
  // signals add the interruption marker only once per assistant turn.
  // #126: additional stream-activity sources beyond message content — tool
  // lifecycle, background-job/heartbeat events, and job completions.
  const toolExecutions = useChatStore((state) => state.toolExecutions);
  const backgroundJobs = useChatStore((state) => state.backgroundJobs);
  const addBackgroundJob = useChatStore((state) => state.addBackgroundJob);
  const updateBackgroundJob = useChatStore(
    (state) => state.updateBackgroundJob,
  );
  const [activeJobsStatusUnavailable, setActiveJobsStatusUnavailable] =
    useState(false);
  // mmagent-chat-ui >=0.1.83: monotonic counter bumped on every HEARTBEAT frame
  // (the only signal for a content-silent, status-stable long inline tool).
  // TODO(#126): drop the cast once 0.1.84 fixes the package type-surface —
  // heartbeatCount IS present at runtime (0.1.83 dist/index.js), but the barrel
  // re-export doesn't carry it on ChatState under moduleResolution:"node". The
  // `?? 0` can never fire in practice; if it ever did, the signature degrades
  // gracefully to the broadened tool/job serialization layer.
  const heartbeatCount = useChatStore(
    (s) => (s as { heartbeatCount?: number }).heartbeatCount ?? 0,
  );

  // Track loading state changes
  useEffect(() => {
    if (isLoading && loadingStartTimeRef.current === null) {
      loadingStartTimeRef.current = Date.now();
      lastActivityTimeRef.current = Date.now();
      recordStreamActivityRef.current();
    } else if (!isLoading) {
      loadingStartTimeRef.current = null;
    }
  }, [isLoading]);

  // Track ANY stream event as activity (#124/#126): content deltas, tool
  // lifecycle, background-job/heartbeat events, and job completions all fold
  // into one signature. When it changes, the stream is alive — a content-silent
  // tool batch no longer looks stuck.
  useEffect(() => {
    const signature = computeStreamActivitySignature({
      messages,
      toolExecutions,
      backgroundJobs,
      heartbeatCount,
    });
    if (signature !== lastActivitySignatureRef.current) {
      lastActivitySignatureRef.current = signature;
      lastActivityTimeRef.current = Date.now();
      recordStreamActivityRef.current();
    }
  }, [messages, toolExecutions, backgroundJobs, heartbeatCount]);

  const selectedConversationSummary = conversations.find(
    (summary) => String(summary.id) === String(conversationId),
  );
  const selectedAgentTurnActive =
    workspaceMode === "agent" &&
    (selectedConversationSummary?.agentStatus === "generating" ||
      selectedConversationSummary?.agentStatus === "running_tool");
  const hasStreamingAssistantMessage = messages.some(
    (message) =>
      message.role === "assistant" &&
      ((message as any).isStreaming === true ||
        (message as any).isWorking === true),
  );
  const conversationStreamActive =
    isLoading || selectedAgentTurnActive || hasStreamingAssistantMessage;

  // Detect stuck streams and provide recovery
  useEffect(() => {
    if (!conversationStreamActive) return;
    if (selectedAgentTurnActive && !wasAgentTurnActiveRef.current) {
      lastActivityTimeRef.current = Date.now();
    }
    wasAgentTurnActiveRef.current = selectedAgentTurnActive;

    const checkInterval = setInterval(() => {
      if (document.hidden) return;
      const now = Date.now();
      const timeSinceActivity = now - lastActivityTimeRef.current;
      const timeSinceStart = loadingStartTimeRef.current
        ? now - loadingStartTimeRef.current
        : 0;

      // Stuck only if loading past the 10-minute floor AND no stream event of
      // any kind in the last 2 minutes (#124/#126). A stream still emitting
      // tool/heartbeat/job events keeps timeSinceActivity low and is never
      // cancelled, however old it is.
      if (isLoading && isStreamStuck(timeSinceStart, timeSinceActivity)) {
        console.warn(
          "[IDA Chat] Stream appears stuck (no activity for 2+ minutes after 10+ minutes loading). Attempting recovery.",
        );
        // Cancel the stream and reset loading state
        cancelStream();
        // Task #497 A1: append an explicit interruption marker to the last
        // assistant message so the user sees the failure instead of a
        // silently-truncated response. Mirrors the useSendMessageWithFiles
        // network-drop marker so the surface is uniform across both stream
        // consumers (library-managed in Chat.tsx, custom in
        // useSendMessageWithFiles for file uploads).
        const liveMessages = messagesRef.current;
        const lastAssistant = [...liveMessages]
          .reverse()
          .find((m) => m.role === "assistant");
        if (lastAssistant) {
          appendStreamInterruptedMarkerOnce(lastAssistant.id);
        }
        // Force reset loading state in case cancelStream doesn't work
        setTimeout(() => {
          if (loadingStartTimeRef.current !== null) {
            console.warn("[IDA Chat] Force-resetting loading state");
            setLoading(false);
            loadingStartTimeRef.current = null;
          }
        }, 1000);
      }
    }, STREAM_CHECK_INTERVAL_MS);

    return () => clearInterval(checkInterval);
  }, [
    conversationStreamActive,
    selectedAgentTurnActive,
    isLoading,
    cancelStream,
    setLoading,
  ]);
  const { startNewConversation } = useLoadConversation();
  const clearMessages = useChatStore((state) => state.clearMessages);
  const deleteLocalMessagesFrom = useChatStore(
    (state) => state.deleteMessagesFrom,
  );
  const setMessages = useChatStore((state) => state.setMessages);
  const setActiveConversationId = useChatStore(
    (state) => state.setConversationId,
  );
  const messagesRef = useRef(messages);
  const {
    messages: voiceLiveMessages,
    updateTranscriptTurns: updateGptLiveTranscriptTurns,
  } = useGptLiveTranscriptMessages(messages, conversationId, currentUser?.id);
  const [gptLiveVoicePhase, setGptLiveVoicePhase] =
    useState<GptLiveVoicePhase | null>(null);
  const [gptLiveVoiceFeedback, setGptLiveVoiceFeedback] =
    useState<GptLiveVoiceFeedback | null>(null);
  const assistantRowDiagnosticStateRef = useRef(new Map<string, string>());
  useEffect(() => {
    setGptLiveVoiceFeedback(null);
    assistantRowDiagnosticStateRef.current.clear();
  }, [conversationId]);
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    const nextState = new Map<string, string>();
    messages.forEach((message) => {
      if (message.role !== "assistant") return;
      const turnId = message.metadata?.assistant_turn_id;
      const turnIdHash =
        typeof turnId === "string" ? hashDiagnosticId(turnId) : null;
      const rowId = String(message.id);
      const rowIdHash = hashDiagnosticId(rowId);
      const isStreaming = message.isStreaming === true;
      const isWorking = message.isWorking === true;
      const signature = `${turnIdHash ?? "none"}:${isStreaming}:${isWorking}`;
      nextState.set(rowId, signature);
      if (assistantRowDiagnosticStateRef.current.get(rowId) === signature) {
        return;
      }
      console.debug("[IDA Chat] assistant row state changed", {
        row_id_hash: rowIdHash,
        assistant_turn_id_hash: turnIdHash,
        isStreaming,
        isWorking,
      });
    });
    assistantRowDiagnosticStateRef.current.forEach((_signature, rowId) => {
      if (nextState.has(rowId)) return;
      console.debug("[IDA Chat] assistant row removed", {
        row_id_hash: hashDiagnosticId(rowId),
      });
    });
    assistantRowDiagnosticStateRef.current = nextState;
  }, [messages, conversationId]);
  const [visibleMessagesConversationId, setVisibleMessagesConversationId] =
    useState<number | null>(() => {
      const initialId = useChatStore.getState().conversationId;
      const parsed = Number(initialId);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    });
  const conversationIdRef = useRef(conversationId);
  const messageWindowRef = useRef<ConversationMessageWindow | null>(null);
  const messageWindowConversationIdRef = useRef<number | null>(null);
  const messageLoadGenerationRef = useRef(0);
  const olderMessageRequestRef = useRef<{
    conversationId: number;
    beforeId: number;
    generation: number;
  } | null>(null);
  const olderMessageRequestWaitersRef = useRef<Set<() => void>>(new Set());
  const notifyOlderMessageRequestComplete = useCallback(() => {
    const waiters = Array.from(olderMessageRequestWaitersRef.current);
    olderMessageRequestWaitersRef.current.clear();
    waiters.forEach((resolve) => resolve());
  }, []);
  const waitForOlderMessageRequest = useCallback(async () => {
    const deadline = Date.now() + STREAM_RECONCILE_REQUEST_TIMEOUT_MS;
    while (olderMessageRequestRef.current) {
      let waiter!: () => void;
      const completion = new Promise<void>((resolve) => {
        waiter = resolve;
        olderMessageRequestWaitersRef.current.add(resolve);
        if (!olderMessageRequestRef.current) {
          olderMessageRequestWaitersRef.current.delete(resolve);
          resolve();
        }
      });
      const remaining = Math.max(0, deadline - Date.now());
      const completed = await new Promise<boolean>((resolve) => {
        const timeoutId = window.setTimeout(() => resolve(false), remaining);
        void completion.then(() => {
          window.clearTimeout(timeoutId);
          resolve(true);
        });
      });
      if (!completed) {
        olderMessageRequestWaitersRef.current.delete(waiter);
        return false;
      }
    }
    return true;
  }, []);
  const deleteMessageInFlightRef = useRef(false);
  const pollInFlightRef = useRef(false);
  const pollPromiseRef = useRef<{
    conversationId: number;
    promise: Promise<boolean>;
  } | null>(null);
  const pollOwnerRef = useRef<object | null>(null);
  const pollConversationMessagesRef = useRef<
    | ((options?: {
        force?: boolean;
        bypassInFlight?: boolean;
      }) => Promise<boolean>)
    | null
  >(null);
  const pollIntervalRef = useRef<number | null>(null);
  // Set alongside messagesSyncError. startMessagePolling checks this so a
  // re-render (or a passive-stream reconcile request) cannot silently
  // restart the interval this conversation was halted from.
  const pollHaltedRef = useRef(false);
  const collaboratorFetchInFlightRef = useRef<Set<number>>(new Set());
  const previousConversationIdRef = useRef<string | number | null>(
    conversationId,
  );
  const previousIsLoadingRef = useRef(isLoading);

  useHeartbeatCompletionSignal(messages as IDAChatMessage[], conversationId);

  // Used only for non-background message edits. Package-generated job completion
  // rows stay in the store for internal lifecycle handling but are not rendered.
  const updateMessage = useChatStore((state) => state.updateMessage);

  const submittedAssistantMessageIdRef = useRef<string | null>(null);
  const handleSendMessage = useCallback(
    (
      content: string,
      images?: ImageReference[],
      onAccepted?: () => void,
      sendIntent?: "workspace_onboarding_trigger",
    ) => {
      setUserStoppedConversationId(null);
      if (
        workspaceMode === "agent" &&
        !useChatStore.getState().conversationId &&
        content !== AGENT_ONBOARDING_TRIGGER_MESSAGE &&
        content !== WORKSPACE_ONBOARDING_TRIGGER_MESSAGE
      ) {
        return Promise.reject(
          new Error(
            "Select an agent or use New Agent before sending a message.",
          ),
        );
      }
      freshChatConversationGuardRef.current.prepareSend(
        useChatStore.getState().conversationId,
        () => useChatStore.getState().setConversationId(null),
      );
      const existingMessageIds = new Set(
        useChatStore.getState().messages.map((message) => message.id),
      );
      const composerDraft = pendingComposerDraftRef.current;
      const capturedDraft =
        composerDraft?.draft === content ? composerDraft : null;
      if (capturedDraft) capturedDraft.sending = true;
      const send = () => sendMessage(content, undefined, images, onAccepted);
      const isSetupConversation = shouldSendWorkspaceOnboardingHeaders({
        needsOnboarding,
        workspaceMode,
        onboardingAgentId,
        conversationId: useChatStore.getState().conversationId,
      });
      const pending =
        sendIntent === "workspace_onboarding_trigger" || isSetupConversation
          ? sendWithWorkspaceOnboardingHeaders(
              authHeaders,
              onboardingWorkspaceName,
              send,
            )
          : send();
      submittedAssistantMessageIdRef.current =
        useChatStore
          .getState()
          .messages.find(
            (message) =>
              message.role === "assistant" &&
              !existingMessageIds.has(message.id),
          )?.id ?? null;
      if (capturedDraft) {
        capturedDraft.assistantMessageId =
          submittedAssistantMessageIdRef.current;
      }
      const optimisticMessage = getOptimisticCurrentUserMetadata(
        useChatStore.getState().messages,
        existingMessageIds,
        currentUser?.id,
      );
      if (optimisticMessage) {
        updateMessage(optimisticMessage.id, {
          metadata: optimisticMessage.metadata,
        } as Partial<ChatMessageWithFiles>);
      }
      if (!capturedDraft) return pending;
      return Promise.resolve(pending).catch((error: unknown) => {
        if (pendingComposerDraftRef.current === capturedDraft) {
          pendingComposerDraftRef.current = null;
        }
        throw error;
      });
    },
    [
      authHeaders,
      currentUser?.id,
      needsOnboarding,
      onboardingAgentId,
      onboardingWorkspaceName,
      sendMessage,
      freshChatConversationGuardRef,
      pendingComposerDraftRef,
      updateMessage,
      workspaceMode,
    ],
  );

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    if (conversationId === null) return;
    lastActivityTimeRef.current = Date.now();
    return subscribeToConversationSseActivity((streamConversationId) => {
      if (streamConversationId !== String(conversationId)) return;
      lastActivityTimeRef.current = Date.now();
      recordStreamActivityRef.current();
    });
  }, [conversationId]);

  useEffect(() => {
    const handleStreamRecoverySignal = () => {
      lastActivityTimeRef.current = Date.now();
      setStreamRecoverySignal((current) => current + 1);
    };
    window.addEventListener(
      "ida-conversation-stream-recovery",
      handleStreamRecoverySignal,
    );
    return () =>
      window.removeEventListener(
        "ida-conversation-stream-recovery",
        handleStreamRecoverySignal,
      );
  }, []);

  const getFreshAuthHeaders = useCallback(async () => {
    const headers = await getAuthHeaders();
    return { ...headers, "X-Workspace-Id": workspaceId };
  }, [getAuthHeaders, workspaceId]);

  useBackgroundJobProgressResilience({
    apiBaseUrl: `${getApiBaseUrl()}/api/workspace/ida`,
    getAuthHeaders: getFreshAuthHeaders,
    reportDocumentCardCompletionDiagnostic,
  });

  const refreshConversations = useCallback(
    async ({
      search = "",
      skip = 0,
    }: {
      search?: string;
      skip?: number;
    } = {}) => {
      setConversationsLoading(true);
      try {
        const response = await listConversationSummaries({
          workspaceId,
          search,
          skip,
          limit: workspaceMode === "agent" ? AGENT_INVENTORY_LIMIT : 25,
        });

        setConversationSearch(search);
        setConversationTotal(response.total);
        if (skip === 0 && search === "") {
          setConversationInventoryWorkspaceId(workspaceId);
        }
        setConversations((current) => {
          if (skip === 0) {
            return workspaceMode === "agent"
              ? mergeAgentStatusSnapshot(current, response.items)
              : response.items;
          }

          const merged = new Map<number, CollaborationConversationSummary>();
          current.forEach((item) => merged.set(item.id, item));
          response.items.forEach((item) => merged.set(item.id, item));
          return Array.from(merged.values()).sort((left, right) =>
            (right.updatedAt || right.createdAt).localeCompare(
              left.updatedAt || left.createdAt,
            ),
          );
        });
        return true;
      } catch (err) {
        console.warn("[IDA Chat] Conversation list fetch failed:", err);
        return false;
      } finally {
        setConversationsLoading(false);
      }
    },
    [listConversationSummaries, workspaceId, workspaceMode],
  );

  const fetchAgentStatusSummaries = useCallback(async () => {
    const response = await listConversationSummaries({
      workspaceId,
      // Status reconciliation must cover every visible agent. Agent Mode
      // has no search surface, and carrying a previous chat search here can
      // omit tiles from the durable fallback snapshot.
      search: "",
      skip: 0,
      // Fetch the first page, then page through every tile already loaded
      // into the strip so status reconciliation keeps covering the full
      // visible roster.
      limit: AGENT_INVENTORY_LIMIT,
    });
    const items = [...response.items];
    const loadedCount = Math.min(conversations.length, response.total);
    let skip = items.length;
    while (skip < loadedCount) {
      const page = await listConversationSummaries({
        workspaceId,
        search: "",
        skip,
        limit: Math.min(AGENT_INVENTORY_LIMIT, loadedCount - skip),
      });
      if (page.items.length === 0) break;
      items.push(...page.items);
      skip += page.items.length;
    }
    return { ...response, items };
  }, [conversations.length, listConversationSummaries, workspaceId]);

  const handleAgentStatusRefresh = useCallback(
    (response: CollaborationConversationListResponse) => {
      setConversationTotal(response.total);
      setConversations((current) => {
        const refreshed = mergeAgentStatusSnapshot(current, response.items);
        const refreshedIds = new Set(response.items.map((item) => item.id));
        return [
          ...refreshed,
          ...current.filter((item) => !refreshedIds.has(item.id)),
        ];
      });
    },
    [],
  );

  const { reconcileNow: reconcileAgentStatuses } = useAgentStatusPolling({
    enabled: workspaceMode === "agent",
    fetchSummaries: fetchAgentStatusSummaries,
    onRefresh: handleAgentStatusRefresh,
  });

  const handleAgentStatusEvent = useCallback(
    (event: AgentStatusStreamEvent) => {
      setConversations((current) =>
        applyAgentStatusStreamEvent(current, event),
      );
    },
    [],
  );

  const { pushActive: agentStatusPushActive } = useAgentStatusStream({
    apiBaseUrl: `${getApiBaseUrl()}/api/workspace/ida`,
    workspaceKey: workspaceId,
    getAuthHeaders: getFreshAuthHeaders,
    enabled: workspaceMode === "agent",
    onAgentStatus: handleAgentStatusEvent,
    // LISTEN/NOTIFY has no replay. A transition may be missed while this
    // client is disconnected, so every successful (re)connect reconciles
    // against the durable summary endpoint before relying on live frames.
    onConnected: reconcileAgentStatuses,
  });

  useEffect(() => {
    void refreshConversations({ search: "", skip: 0 });
    setConversationCollaboratorsById({});
  }, [refreshConversations, workspaceId]);

  useEffect(() => {
    const previousConversationId = previousConversationIdRef.current;
    previousConversationIdRef.current = conversationId;

    if (previousConversationId === conversationId) return;
    void refreshConversations({ search: conversationSearch, skip: 0 });
  }, [conversationId, conversationSearch, refreshConversations]);

  useEffect(() => {
    const wasLoading = previousIsLoadingRef.current;
    previousIsLoadingRef.current = isLoading;

    if (wasLoading && !isLoading) {
      void refreshConversations({ search: conversationSearch, skip: 0 });
    }
  }, [conversationSearch, isLoading, refreshConversations]);

  useEffect(() => {
    if (!collaborationEnabled) {
      setCurrentUser(null);
      setWorkspaceUsers([]);
      return;
    }

    let active = true;
    const loadCollaborationContext = async () => {
      try {
        const [userData, workspaceUserData] = await Promise.all([
          readUser(),
          listWorkspaceUsers(workspaceId),
        ]);
        if (!active) return;
        setCurrentUser(userData);
        setWorkspaceUsers(workspaceUserData);
      } catch (err) {
        console.warn("[IDA Chat] Collaboration context fetch failed:", err);
        if (!active) return;
        setCurrentUser(null);
        setWorkspaceUsers([]);
      }
    };

    loadCollaborationContext();
    return () => {
      active = false;
    };
  }, [collaborationEnabled, listWorkspaceUsers, readUser, workspaceId]);

  useEffect(() => {
    const activeConversationId =
      typeof conversationId === "number"
        ? conversationId
        : typeof conversationId === "string"
          ? Number.parseInt(conversationId, 10)
          : NaN;

    if (!collaborationEnabled || !Number.isFinite(activeConversationId)) {
      setCurrentConversationCollaborators([]);
      setConversationCollaboratorsError(null);
      setConversationCollaboratorsLoading(false);
      return;
    }

    let active = true;
    setConversationCollaboratorsLoading(true);
    setConversationCollaboratorsError(null);

    listConversationCollaborators(workspaceId, activeConversationId)
      .then((collaborators) => {
        if (!active) return;
        setCurrentConversationCollaborators(collaborators);
        setConversationCollaboratorsById((current) => ({
          ...current,
          [activeConversationId]: collaborators,
        }));
      })
      .catch((err: any) => {
        if (!active) return;
        setCurrentConversationCollaborators([]);
        setConversationCollaboratorsError(
          err?.message || "Failed to load collaborators.",
        );
      })
      .finally(() => {
        if (active) {
          setConversationCollaboratorsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    collaborationEnabled,
    conversationId,
    listConversationCollaborators,
    workspaceId,
  ]);

  useEffect(() => {
    if (!collaborationEnabled) return;

    const sharedConversationIds = conversations
      .filter(
        (conversation) =>
          conversation.userRole === "collaborator" &&
          !conversationCollaboratorsById[conversation.id] &&
          !collaboratorFetchInFlightRef.current.has(conversation.id),
      )
      .map((conversation) => conversation.id);

    if (sharedConversationIds.length === 0) return;

    sharedConversationIds.forEach((id) =>
      collaboratorFetchInFlightRef.current.add(id),
    );

    void Promise.all(
      sharedConversationIds.map(async (id) => {
        try {
          const collaborators = await listConversationCollaborators(
            workspaceId,
            id,
          );
          return { id, collaborators };
        } catch (err) {
          console.warn(
            "[IDA Chat] Shared conversation collaborator fetch failed:",
            err,
          );
          return { id, collaborators: [] as ConversationCollaborator[] };
        }
      }),
    ).then((results) => {
      setConversationCollaboratorsById((current) => {
        const next = { ...current };
        results.forEach(({ id, collaborators }) => {
          next[id] = collaborators;
          collaboratorFetchInFlightRef.current.delete(id);
        });
        return next;
      });
    });
  }, [
    collaborationEnabled,
    conversations,
    conversationCollaboratorsById,
    listConversationCollaborators,
    workspaceId,
  ]);

  const publishConversationMessageWindow = useCallback(
    (
      targetConversationId: number,
      window: ConversationMessageWindow,
      {
        replaceVisible = false,
        messageIdsBeforeSelection,
        messagesAtSelectionById,
      }: {
        replaceVisible?: boolean;
        messageIdsBeforeSelection?: ReadonlySet<string>;
        messagesAtSelectionById?: ReadonlyMap<string, IDAChatMessage>;
      } = {},
    ) => {
      messageWindowRef.current = window;
      messageWindowConversationIdRef.current = targetConversationId;
      setVisibleMessagesConversationId(targetConversationId);
      setHasOlderMessages(window.hasMore);

      const mappedMessages = mapConversationMessageWindow(window);
      const nextMessages = replaceVisible
        ? messageIdsBeforeSelection
          ? mergeConversationHistoryWithLiveMessages(
              useChatStore.getState().messages as IDAChatMessage[],
              mappedMessages,
              messageIdsBeforeSelection,
              messagesAtSelectionById ?? new Map(),
            )
          : mappedMessages
        : mergeConversationMessages(
            messagesRef.current as IDAChatMessage[],
            mappedMessages,
          );
      messagesRef.current = nextMessages;
      setMessages(nextMessages);
      return nextMessages;
    },
    [setMessages],
  );

  const resetConversationMessageWindow = useCallback(() => {
    messageLoadGenerationRef.current += 1;
    messageWindowRef.current = null;
    messageWindowConversationIdRef.current = null;
    setVisibleMessagesConversationId(null);
    olderMessageRequestRef.current = null;
    notifyOlderMessageRequestComplete();
    setHasOlderMessages(false);
    setOlderMessagesLoading(false);
    setOlderMessagesLoadFailed(false);
  }, [notifyOlderMessageRequestComplete]);

  // Tracks the conversation id whose most recent history load failed (e.g.
  // a Hub gateway timeout) and whether a load is currently in flight, plus
  // the generation each transition was made at so a superseded load's
  // result cannot overwrite a newer one's. Scoped to a conversation id, not
  // a plain boolean, so a stale failure from a previously viewed
  // conversation cannot bleed into the display of a conversation that is
  // still loading; `loading` takes precedence over the load-failed surface
  // for the same reason. Extracted to reduceHistoryLoadState so these
  // race-sensitive transitions are unit-testable without rendering this
  // component.
  const [historyLoadState, dispatchHistoryLoadEvent] = useReducer(
    reduceHistoryLoadState,
    initialHistoryLoadState,
  );

  const fetchConversationMessagePage = useCallback(
    async (
      targetConversationId: number,
      beforeId?: number,
      signal?: AbortSignal,
      includeActiveJobs = false,
    ) => {
      const headers = await getFreshAuthHeaders();
      const requestId = createFrontendRequestId();
      const requestHeaders = new Headers(headers);
      requestHeaders.set("X-Request-ID", requestId);
      const endpoint = `${getApiBaseUrl()}/api/workspace/ida/chat/conversations/${targetConversationId}/messages`;
      let response: Response;
      try {
        response = await fetch(
          buildConversationMessagePageUrl(endpoint, beforeId, includeActiveJobs),
          { headers: requestHeaders, signal },
        );
      } catch (error) {
        if (!signal?.aborted) {
          reportChatHistoryFailure("conversation_messages", {
            requestId,
            kind: "history_get",
            phase: "fetch_rejected",
          });
        }
        throw error;
      }

      if (!response.ok) {
        const error = new Error(
          `Failed to fetch conversation messages: ${response.status}`,
        ) as Error & { status?: number };
        error.status = response.status;
        reportChatHistoryFailure("conversation_messages", {
          requestId,
          kind: "history_get",
          phase: "http_response",
          status: response.status,
        });
        throw error;
      }

      try {
        const payload = await response.json();
        return parseConversationMessagePage(payload, { includeActiveJobs });
      } catch (error) {
        if (!signal?.aborted) {
          reportChatHistoryFailure("conversation_messages", {
            requestId,
            kind: "history_get",
            phase: "response_read",
            status: response.status,
          });
        }
        throw error;
      }
    },
    [getFreshAuthHeaders, reportChatHistoryFailure],
  );

  const restoreActiveConversationJobs = useCallback(
    (page: ReturnType<typeof parseConversationMessagePage>) => {
      if (page.activeJobsError) {
        setActiveJobsStatusUnavailable(true);
        return;
      }
      setActiveJobsStatusUnavailable(false);
      if (!page.activeJobs) return;
      const valid = upsertActiveConversationJobs(
        page.activeJobs,
        useChatStore.getState().backgroundJobs,
        {
          add: addBackgroundJob,
          update: updateBackgroundJob,
        },
      );
      if (!valid) setActiveJobsStatusUnavailable(true);
    },
    [addBackgroundJob, updateBackgroundJob],
  );

  const loadConversation = useCallback(
    async (targetConversationId: number) => {
      // A selected conversation replaces the pending new-agent destination.
      // Do not let its delayed onboarding send run after navigation.
      cancelPendingAgentCreation();
      const loadGeneration = messageLoadGenerationRef.current + 1;
      messageLoadGenerationRef.current = loadGeneration;
      setActiveJobsStatusUnavailable(false);
      const currentlyVisibleConversationId =
        messageWindowConversationIdRef.current ?? visibleMessagesConversationId;
      const messagesAtSelectionById = new Map(
        messagesRef.current.map((message) => [message.id, message]),
      );
      const messageIdsBeforeSelection =
        currentlyVisibleConversationId === targetConversationId
          ? new Set<string>()
          : new Set(messagesRef.current.map((message) => message.id));
      dispatchHistoryLoadEvent({
        type: "loadStarted",
        conversationId: targetConversationId,
        generation: loadGeneration,
      });
      try {
        const page = await requestConversationPageForSelection(
          targetConversationId,
          setConversationForSelection,
          waitForConversationSubscription,
          (conversationId) =>
            fetchConversationMessagePage(
              conversationId,
              undefined,
              undefined,
              true,
            ),
        );
        if (messageLoadGenerationRef.current !== loadGeneration) return [];
        restoreActiveConversationJobs(page);

        olderMessageRequestRef.current = null;
        notifyOlderMessageRequestComplete();
        setOlderMessagesLoading(false);
        let mappedMessages = publishConversationMessageWindow(
          targetConversationId,
          replaceConversationMessageWindow(page),
          {
            replaceVisible: true,
            messageIdsBeforeSelection,
            messagesAtSelectionById,
          },
        );

        // FE #1482: when the newest page holds only hidden agent rows, the
        // mapped window is empty even though older pages contain the user's
        // real messages. Walk back automatically, bounded, instead of
        // opening on an empty chat behind "Load earlier".
        if (
          mappedMessages.length === 0 &&
          messageWindowRef.current?.hasMore &&
          messageWindowRef.current?.nextBeforeId !== null &&
          !olderMessageRequestRef.current
        ) {
          const currentWindow = messageWindowRef.current;
          // `nextBeforeId` is non-null here (guarded by the surrounding
          // condition); the ref's `beforeId` is a non-null number.
          const backfillCursor = currentWindow.nextBeforeId as number;
          const request = {
            conversationId: targetConversationId,
            beforeId: backfillCursor,
            generation: loadGeneration,
          };
          olderMessageRequestRef.current = request;
          setOlderMessagesLoading(true);
          try {
            const backfilled =
              await backfillConversationMessageWindowUntilVisible({
                window: currentWindow,
                fetchOlder: (beforeId) =>
                  fetchConversationMessagePage(targetConversationId, beforeId),
                onWindowChange: (nextWindow) => {
                  // Stop publishing as soon as this load is superseded or the
                  // user hits Stop-equivalent state; the loop itself keeps
                  // fetching only while the window still maps to nothing.
                  if (
                    olderMessageRequestRef.current !== request ||
                    messageLoadGenerationRef.current !== loadGeneration ||
                    messageWindowConversationIdRef.current !==
                      targetConversationId
                  ) {
                    return;
                  }
                  mappedMessages = publishConversationMessageWindow(
                    targetConversationId,
                    nextWindow,
                    {
                      replaceVisible: true,
                      messageIdsBeforeSelection,
                      messagesAtSelectionById,
                    },
                  );
                },
              });
            if (
              olderMessageRequestRef.current === request &&
              messageLoadGenerationRef.current === loadGeneration &&
              messageWindowConversationIdRef.current === targetConversationId
            ) {
              mappedMessages = publishConversationMessageWindow(
                targetConversationId,
                backfilled.window,
                {
                  replaceVisible: true,
                  messageIdsBeforeSelection,
                  messagesAtSelectionById,
                },
              );
            }
          } catch (err) {
            // The initial page is already published; a failed backfill leaves
            // the empty-with-"Load earlier" state, which the manual button
            // can still recover from.
            console.warn("[IDA Chat] Hidden-page backfill failed:", err);
          } finally {
            if (olderMessageRequestRef.current === request) {
              olderMessageRequestRef.current = null;
              notifyOlderMessageRequestComplete();
              setOlderMessagesLoading(false);
            }
          }
        }

        dispatchHistoryLoadEvent({
          type: "loadSucceeded",
          conversationId: targetConversationId,
          generation: loadGeneration,
        });
        return mappedMessages;
      } catch (err) {
        // A stale (superseded) generation's failure must not overwrite the
        // load-failed flag once a newer load has already resolved; the
        // reducer's own generation check would drop it too, but skipping
        // the dispatch here also preserves the throw's timing.
        if (messageLoadGenerationRef.current !== loadGeneration) {
          throw err;
        }
        const markFailed = !(err as { message?: string })?.message?.includes(
          "not configured",
        );
        dispatchHistoryLoadEvent({
          type: "loadFailed",
          conversationId: targetConversationId,
          generation: loadGeneration,
          markFailed,
        });
        throw err;
      }
    },
    [
      cancelPendingAgentCreation,
      fetchConversationMessagePage,
      restoreActiveConversationJobs,
      notifyOlderMessageRequestComplete,
      publishConversationMessageWindow,
      setConversationForSelection,
      visibleMessagesConversationId,
      waitForConversationSubscription,
    ],
  );

  const pendingGptLiveConversationRef = useRef<Promise<number | null> | null>(
    null,
  );
  const ensureGptLiveConversation = useCallback((): Promise<number | null> => {
    const selectedId = useChatStore.getState().conversationId;
    const parsedSelectedId =
      selectedId === null ? NaN : Number.parseInt(String(selectedId), 10);
    if (Number.isSafeInteger(parsedSelectedId) && parsedSelectedId > 0) {
      return Promise.resolve(parsedSelectedId);
    }
    if (pendingGptLiveConversationRef.current) {
      return pendingGptLiveConversationRef.current;
    }

    const pending = (async () => {
      const createdId = await createEmptyChatConversation(workspaceId);
      return loadCreatedGptLiveConversationAndRefreshInBackground(
        createdId,
        () => useChatStore.getState().conversationId,
        loadConversation,
        () => refreshConversations({ search: conversationSearch, skip: 0 }),
        (abandonedConversationId) =>
          console.info(
            "[IDA Chat] GPT-Live conversation creation completed after selection changed",
            { created_conversation_id: abandonedConversationId },
          ),
      );
    })();
    pendingGptLiveConversationRef.current = pending;
    const clearPending = () => {
      if (pendingGptLiveConversationRef.current === pending) {
        pendingGptLiveConversationRef.current = null;
      }
    };
    void pending.then(clearPending, clearPending);
    return pending;
  }, [
    conversationSearch,
    createEmptyChatConversation,
    loadConversation,
    refreshConversations,
    workspaceId,
  ]);

  // Re-attempt loading the conversation whose most recent history fetch
  // failed. A no-op once that conversation is no longer the one flagged as
  // failed (e.g. the user already navigated away).
  const handleRetryHistoryLoad = useCallback(() => {
    if (historyLoadState.loadFailedConversationId === null) return;
    void loadConversation(historyLoadState.loadFailedConversationId);
  }, [historyLoadState.loadFailedConversationId, loadConversation]);

  // Persist conversationId to sessionStorage so it survives page refresh
  const sessionStorageKey = `ida-conversation-${workspaceId}`;

  useEffect(() => {
    if (conversationId) {
      sessionStorage.setItem(sessionStorageKey, String(conversationId));
    }
  }, [conversationId, sessionStorageKey]);

  // Restore conversation from sessionStorage on mount.
  // Read the saved ID synchronously via useState initializer (runs once, strict-mode safe),
  // then load it in an effect.
  const [savedConversationId, setSavedConversationId] = useState(() => {
    if (needsOnboarding || workspaceMode === "agent") return null;
    const saved = sessionStorage.getItem(sessionStorageKey);
    if (saved) {
      const numId = parseInt(saved, 10);
      return isNaN(numId) ? null : numId;
    }
    return null;
  });

  // A 404 for a conversation is a definite answer: it was deleted. Forget it
  // and return to the empty new-chat state instead of dead-ending on the
  // load-failed panel or polling it forever (FE #1561).
  const forgetMissingConversation = useCallback(
    (missingConversationId: number) => {
      if (String(conversationIdRef.current) !== String(missingConversationId)) {
        return;
      }
      messageLoadGenerationRef.current += 1;
      resetConversationMessageWindow();
      messagesRef.current = [];
      clearMessages();
      setActiveConversationId(null);
      setCurrentConversationCollaborators([]);
      setSavedConversationId(null);
      sessionStorage.removeItem(sessionStorageKey);
      dispatchHistoryLoadEvent({
        type: "newConversation",
        generation: messageLoadGenerationRef.current,
      });
    },
    [
      clearMessages,
      resetConversationMessageWindow,
      sessionStorageKey,
      setActiveConversationId,
    ],
  );
  const forgetMissingConversationRef = useRef(forgetMissingConversation);
  forgetMissingConversationRef.current = forgetMissingConversation;

  useEffect(() => {
    if (workspaceMode === "agent" || !savedConversationId || conversationId)
      return;

    loadConversation(savedConversationId).catch((err) => {
      if (isConversationNotFound(err)) {
        forgetMissingConversationRef.current(savedConversationId);
        return;
      }
      handleRestoreFailure(err, sessionStorageKey);
    });
  }, [
    savedConversationId,
    conversationId,
    loadConversation,
    sessionStorageKey,
    workspaceMode,
  ]);

  useEffect(() => {
    const initialConversationId = initialConversationIdRef.current;
    if (
      initialConversationId == null ||
      initialConversationHydrationStartedRef.current ||
      sessionStorage.getItem(sessionStorageKey) !==
        String(initialConversationId)
    ) {
      return;
    }
    const parsedConversationId = Number(initialConversationId);
    if (
      !Number.isSafeInteger(parsedConversationId) ||
      parsedConversationId <= 0
    )
      return;
    initialConversationHydrationStartedRef.current = true;
    loadConversation(parsedConversationId).catch((err) => {
      if (isConversationNotFound(err)) {
        forgetMissingConversationRef.current(parsedConversationId);
        return;
      }
      handleRestoreFailure(err, sessionStorageKey);
    });
  }, [loadConversation, sessionStorageKey]);

  useWorkspaceAgentSelection({
    workspaceId,
    enabled: workspaceMode === "agent",
    agents: conversations,
    currentId: conversationId,
    preferredConversationId: needsOnboarding ? onboardingAgentId : null,
    suppressAutoSelection: newAgentSelectionPending,
    loading:
      conversationsLoading ||
      historyLoadState.loading ||
      agentCreationPending ||
      isLoading,
    select: loadConversation,
  });

  useWorkspaceOnboardingChatSelection({
    enabled: Boolean(needsOnboarding) && workspaceMode === "chat",
    workspaceId,
    conversations,
    currentId: conversationId,
    inventoryLoaded: conversationInventoryWorkspaceId === workspaceId,
    isLoading: conversationsLoading || historyLoadState.loading || isLoading,
    suppressAutoSelection:
      onboardingChatAutoSelectionSuppressedWorkspaceId === workspaceId,
    select: loadConversation,
  });

  // Filter out the onboarding trigger message from display
  // Note: We previously used useDeferredValue here but it delayed tool execution badges.
  // The MemoizedChatMessage component now handles performance optimization instead.
  const visibleMessages = useMemo(() => {
    const latestInterruptedAssistantId =
      userStoppedConversationId === String(conversationId)
        ? [...messages]
            .reverse()
            .find(
              (message) =>
                message.role === "assistant" &&
                (message as IDAChatMessage).interrupted,
            )?.id
        : undefined;
    const normalMessages = messages
      .filter(
        (msg) =>
          !(
            msg.role === "user" &&
            msg.content === WORKSPACE_ONBOARDING_TRIGGER_MESSAGE
          ),
      )
      .map((message) =>
        message.id === latestInterruptedAssistantId
          ? { ...message, stoppedByUser: true }
          : message,
      );
    const voiceMessagesForDisplay = applyVoiceAssistantPlaybackState(
      voiceLiveMessages,
      gptLiveVoicePhase === "live-response",
    );
    return prepareChatMessagesForDisplay(
      reconcileToolChips(
        mergeVoiceTranscriptMessages(normalMessages, voiceMessagesForDisplay),
      ),
    );
  }, [
    conversationId,
    messages,
    userStoppedConversationId,
    gptLiveVoicePhase,
    voiceLiveMessages,
  ]);

  const followUpSuggestions = useFollowUpSuggestionChips({
    enabled: followUpSuggestionsEnabled,
    workspaceId,
    conversationId,
    messages: visibleMessages,
    isLoading,
    fetchSuggestions: fetchFollowUpSuggestions,
  });
  const starterSuggestions = useStarterSuggestionChips({
    enabled: followUpSuggestionsEnabled,
    workspaceId,
    isEmptyChat:
      !conversationId &&
      visibleMessages.length === 0 &&
      !isLoading &&
      !historyLoadState.loading,
    fetchSuggestions: fetchStarterSuggestions,
  });
  const suggestionChips =
    visibleMessages.length === 0 ? starterSuggestions : followUpSuggestions;

  const clearMessagePolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const pollConversationMessages = useCallback(
    async ({
      force = false,
      bypassInFlight = false,
    }: { force?: boolean; bypassInFlight?: boolean } = {}) => {
      const activeConversationId =
        typeof conversationId === "number"
          ? conversationId
          : typeof conversationId === "string"
            ? Number.parseInt(conversationId, 10)
            : NaN;

      if (!Number.isFinite(activeConversationId)) return false;
      if (pollInFlightRef.current && !bypassInFlight) {
        if (!force) return false;
        const inFlightPoll = pollPromiseRef.current;
        if (
          !inFlightPoll ||
          inFlightPoll.conversationId !== activeConversationId
        ) {
          return false;
        }
        return coalesceForcedMessagePoll(
          inFlightPoll.promise,
          (inFlightTimedOut) =>
            pollConversationMessagesRef.current?.({
              force: true,
              bypassInFlight: inFlightTimedOut,
            }) ?? Promise.resolve(false),
        );
      }
      let shouldBypassInFlight = bypassInFlight;
      if (olderMessageRequestRef.current) {
        if (!force) return false;
        if (!shouldBypassInFlight) {
          const olderRequestCompleted = await waitForOlderMessageRequest();
          if (!olderRequestCompleted) shouldBypassInFlight = true;
        }
        const latestConversationId = Number(conversationIdRef.current);
        if (latestConversationId !== activeConversationId) return false;
      }
      if (shouldBypassInFlight) {
        // A timed-out request can still settle later. Advance the generation
        // so it cannot publish stale messages over this forced refresh.
        messageLoadGenerationRef.current += 1;
        if (olderMessageRequestRef.current) {
          olderMessageRequestRef.current = null;
          notifyOlderMessageRequestComplete();
          setOlderMessagesLoading(false);
        }
      }
      if (deleteMessageInFlightRef.current) return false;
      if (!force && (document.hidden || isLoading)) {
        return false;
      }

      pollInFlightRef.current = true;
      const pollOwner = {};
      pollOwnerRef.current = pollOwner;
      const pollGeneration = messageLoadGenerationRef.current;
      let pollSucceeded = false;
      let resolvePollPromise!: (loaded: boolean) => void;
      const trackedPollPromise = new Promise<boolean>((resolve) => {
        resolvePollPromise = resolve;
      });
      pollPromiseRef.current = {
        conversationId: activeConversationId,
        promise: trackedPollPromise,
      };

      try {
        const fetchPage = (beforeId?: number) =>
          force
            ? fetchWithAbortTimeout((signal) =>
                fetchConversationMessagePage(
                  activeConversationId,
                  beforeId,
                  signal,
                ),
              )
            : fetchConversationMessagePage(activeConversationId, beforeId);
        const page = await fetchPage();
        const latestConversationId =
          typeof conversationIdRef.current === "number"
            ? conversationIdRef.current
            : typeof conversationIdRef.current === "string"
              ? Number.parseInt(conversationIdRef.current, 10)
              : NaN;

        if (
          latestConversationId !== activeConversationId ||
          messageLoadGenerationRef.current !== pollGeneration
        ) {
          return false;
        }

        const currentWindow =
          messageWindowConversationIdRef.current === activeConversationId
            ? messageWindowRef.current
            : null;
        const nextWindow = currentWindow
          ? mergeNewestConversationMessagePage(currentWindow, page)
          : replaceConversationMessageWindow(page);
        const openedGap =
          nextWindow.cachedOlderSegments.length >
          (currentWindow?.cachedOlderSegments.length ?? 0);
        publishConversationMessageWindow(activeConversationId, nextWindow, {
          replaceVisible: openedGap,
        });

        if (
          nextWindow.cachedOlderSegments.length > 0 &&
          nextWindow.hasMore &&
          nextWindow.nextBeforeId !== null
        ) {
          const request = {
            conversationId: activeConversationId,
            beforeId: nextWindow.nextBeforeId,
            generation: pollGeneration,
          };
          olderMessageRequestRef.current = request;
          setOlderMessagesLoading(true);
          try {
            const reconnected = await reconnectConversationMessageGap(
              nextWindow,
              fetchPage,
            );
            if (
              olderMessageRequestRef.current === request &&
              String(conversationIdRef.current) ===
                String(activeConversationId) &&
              messageLoadGenerationRef.current === pollGeneration &&
              messageWindowConversationIdRef.current === activeConversationId
            ) {
              publishConversationMessageWindow(
                activeConversationId,
                reconnected.window,
                { replaceVisible: true },
              );
            }
          } finally {
            if (olderMessageRequestRef.current === request) {
              olderMessageRequestRef.current = null;
              notifyOlderMessageRequestComplete();
              setOlderMessagesLoading(false);
            }
          }
        }
        pollSucceeded = true;
        return true;
      } catch (err) {
        console.warn("[IDA Chat] Conversation polling failed:", err);
        // 413: a stored row exceeds the Hub's page byte ceiling. Every
        // retry hits the identical, deterministic refusal -- #4528 found
        // this looping every 10s indefinitely. Stop for this conversation
        // and say so once, instead of retrying forever.
        if (isConversationNotFound(err)) {
          forgetMissingConversationRef.current(activeConversationId);
        } else if ((err as { status?: number }).status === 413) {
          pollHaltedRef.current = true;
          clearMessagePolling();
          setMessagesSyncError(
            "This conversation has a message too large to sync automatically. " +
              "Already-loaded messages are unaffected; reload the page to check for new ones.",
          );
        }
        return false;
      } finally {
        resolvePollPromise(pollSucceeded);
        if (pollOwnerRef.current === pollOwner) {
          pollInFlightRef.current = false;
          pollOwnerRef.current = null;
        }
        if (pollPromiseRef.current?.promise === trackedPollPromise) {
          pollPromiseRef.current = null;
        }
      }
    },
    [
      conversationId,
      isLoading,
      fetchConversationMessagePage,
      publishConversationMessageWindow,
      clearMessagePolling,
      notifyOlderMessageRequestComplete,
      waitForOlderMessageRequest,
    ],
  );
  pollConversationMessagesRef.current = pollConversationMessages;

  const reconcileSilentConversationStream =
    useCallback(async (): Promise<SilentStreamReconcileResult> => {
      if (streamReconcileInFlightRef.current) return "running";
      const activeConversationId = Number(conversationIdRef.current);
      if (
        !Number.isSafeInteger(activeConversationId) ||
        activeConversationId <= 0
      ) {
        throw new Error("No active conversation to reconcile.");
      }

      streamReconcileInFlightRef.current = true;
      lastActivityTimeRef.current = Date.now();
      try {
        const [messagesLoaded, statusResponse] = await Promise.all([
          pollConversationMessages({ force: true }),
          fetchAgentStatusSummaries(),
        ]);
        if (!messagesLoaded) {
          throw new Error("Could not refresh persisted conversation messages.");
        }
        handleAgentStatusRefresh(statusResponse);
        const summary = statusResponse.items.find(
          (item) => item.id === activeConversationId,
        );
        if (!summary) {
          throw new Error("Conversation status was missing from the refresh.");
        }

        if (isFinalConversationSummary(summary)) {
          // A final summary may describe an earlier turn while a later voice
          // submission is queued. Only stop a local send; the passive stream
          // remains open for conversation-wide queued and running frames.
          const persistedMessagesAtReconcile = useChatStore.getState()
            .messages as IDAChatMessage[];
          await cancelLocalSendForFinalStatus(isSending, cancelStream);
          const reconciledMessages = preserveLiveDocumentExecutions(
            useChatStore.getState().messages as IDAChatMessage[],
            persistedMessagesAtReconcile,
          ) as ChatMessageType[];
          setMessages(
            reconciledMessages.map((message) =>
              message.isStreaming || message.isWorking
                ? { ...message, isStreaming: false, isWorking: false }
                : message,
            ),
          );
          setLoading(false);
          return "finished";
        }
        return "running";
      } finally {
        streamReconcileInFlightRef.current = false;
        lastActivityTimeRef.current = Date.now();
      }
    }, [
      cancelStream,
      isSending,
      fetchAgentStatusSummaries,
      handleAgentStatusRefresh,
      pollConversationMessages,
      setLoading,
      setMessages,
    ]);

  const streamRecovery = useSilentStreamRecovery({
    active: conversationStreamActive,
    conversationKey: String(conversationId ?? ""),
    recoverySignal: streamRecoverySignal,
    reconcile: reconcileSilentConversationStream,
  });
  recordStreamActivityRef.current = streamRecovery.recordActivity;

  const handleLoadOlderMessages = useCallback(async () => {
    const activeConversationId =
      typeof conversationId === "number"
        ? conversationId
        : typeof conversationId === "string"
          ? Number.parseInt(conversationId, 10)
          : NaN;
    const currentWindow =
      messageWindowConversationIdRef.current === activeConversationId
        ? messageWindowRef.current
        : null;

    if (
      !Number.isSafeInteger(activeConversationId) ||
      isLoading ||
      pollInFlightRef.current ||
      !currentWindow?.hasMore ||
      currentWindow.nextBeforeId === null ||
      olderMessageRequestRef.current
    ) {
      return;
    }

    const request = {
      conversationId: activeConversationId,
      beforeId: currentWindow.nextBeforeId,
      generation: messageLoadGenerationRef.current,
    };
    olderMessageRequestRef.current = request;
    setOlderMessagesLoading(true);
    setOlderMessagesLoadFailed(false);

    try {
      const nextWindow =
        currentWindow.cachedOlderSegments.length > 0
          ? (
              await reconnectConversationMessageGap(currentWindow, (beforeId) =>
                fetchConversationMessagePage(activeConversationId, beforeId),
              )
            ).window
          : prependOlderConversationMessagePage(
              currentWindow,
              await fetchConversationMessagePage(
                activeConversationId,
                request.beforeId,
              ),
            );
      if (
        olderMessageRequestRef.current !== request ||
        conversationIdRef.current === null ||
        String(conversationIdRef.current) !== String(activeConversationId) ||
        messageLoadGenerationRef.current !== request.generation ||
        messageWindowConversationIdRef.current !== activeConversationId ||
        messageWindowRef.current !== currentWindow
      ) {
        return;
      }

      publishConversationMessageWindow(activeConversationId, nextWindow, {
        replaceVisible: true,
      });
    } catch (err) {
      console.warn("[IDA Chat] Older conversation page fetch failed:", err);
      if (olderMessageRequestRef.current === request) {
        setOlderMessagesLoadFailed(true);
      }
    } finally {
      if (olderMessageRequestRef.current === request) {
        olderMessageRequestRef.current = null;
        notifyOlderMessageRequestComplete();
        setOlderMessagesLoading(false);
      }
    }
  }, [
    conversationId,
    isLoading,
    fetchConversationMessagePage,
    publishConversationMessageWindow,
    notifyOlderMessageRequestComplete,
  ]);

  const startMessagePolling = useCallback(() => {
    if (pollIntervalRef.current !== null) return;
    if (pollHaltedRef.current) return;
    if (document.hidden || isLoading || !conversationId) return;

    pollIntervalRef.current = window.setInterval(() => {
      void pollConversationMessages();
    }, MESSAGE_POLL_INTERVAL_MS);
  }, [conversationId, isLoading, pollConversationMessages]);

  // A halt from messagesSyncError is per-conversation (that specific
  // conversation has an unsyncable row). Switching conversations must not
  // carry it over and leave the new, healthy conversation's polling halted.
  useEffect(() => {
    pollHaltedRef.current = false;
    setMessagesSyncError(null);
  }, [conversationId]);

  useEffect(() => {
    if (conversationId && !isLoading && !document.hidden) {
      void pollConversationMessages({ force: true });
      startMessagePolling();
    } else {
      clearMessagePolling();
    }

    return clearMessagePolling;
  }, [
    conversationId,
    isLoading,
    pollConversationMessages,
    startMessagePolling,
    clearMessagePolling,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearMessagePolling();
        return;
      }

      if (!conversationId || isLoading) return;

      void pollConversationMessages({ force: true });
      startMessagePolling();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearMessagePolling();
    };
  }, [
    conversationId,
    isLoading,
    pollConversationMessages,
    startMessagePolling,
    clearMessagePolling,
  ]);

  const handleGptLiveTranscriptTurns = useCallback(
    (turns: GptLiveTranscriptTurn[], boundary: GptLiveTranscriptBoundary) => {
      updateGptLiveTranscriptTurns(turns);
      refreshVoiceTranscriptAtBoundary(boundary, () => {
        void pollConversationMessages({ force: true });
      });
    },
    [pollConversationMessages, updateGptLiveTranscriptTurns],
  );

  const attributionTurnSignature = useMemo(() => {
    return messages
      .filter((message) => message.role === "assistant")
      .map((message) =>
        typeof (message as any).hubMessageId === "number"
          ? (message as any).hubMessageId
          : typeof message.id === "number"
            ? message.id
            : null,
      )
      .filter((value): value is number => value !== null && value < 1000000000)
      .sort((a, b) => a - b)
      .join(",");
  }, [messages]);

  const cancelFileStreamRef = useRef<() => void>(() => {});
  const cancelLocalStreams = useCallback(() => {
    settleCancelledToolExecutions(submittedAssistantMessageIdRef.current);
    cancelStream();
    cancelFileStreamRef.current();
  }, [cancelStream]);
  // Stop cancels the run on the server (active-submission cancel route) and ends the stream only
  // after the server confirms: the package's server-confirmed Stop, enabled here for this hook.
  // The provider config itself leaves cancelSubmission off, so useChat().cancelStream, which
  // cancelLocalStreams calls to end the local stream, stays local and does not cancel twice.
  const getChatConfig = useCallback(
    () => withServerStop(useChatStore.getState().config),
    [],
  );
  const getActiveConversationId = useCallback(
    () => conversationIdRef.current ?? null,
    [],
  );
  const {
    stop: handleStopActiveSubmission,
    registerSubmissionId,
    finishStream: finishCancellationStream,
    error: stopError,
    isStopping,
    outcome: stopOutcome,
  } = useActiveSubmissionStop({
    getConfig: getChatConfig,
    getConversationId: getActiveConversationId,
    cancelLocal: cancelLocalStreams,
  });
  const stopWasPendingRef = useRef(false);
  const stopWasRequestedRef = useRef(false);

  useEffect(() => {
    if (isStopping) {
      stopWasPendingRef.current = true;
      return;
    }
    if (!stopWasRequestedRef.current) return;

    const confirmed = isConfirmedUserStop({
      outcome: stopOutcome,
      observedPendingStop: stopWasPendingRef.current,
      error: stopError,
    });
    if (confirmed && stopConversationIdRef.current !== null) {
      setUserStoppedConversationId(stopConversationIdRef.current);
    }
    stopWasPendingRef.current = false;
    stopWasRequestedRef.current = false;
    stopConversationIdRef.current = null;
  }, [conversationId, isStopping, stopError, stopOutcome]);

  const handleStopSubmission = useCallback(async () => {
    stopWasRequestedRef.current = true;
    stopConversationIdRef.current =
      conversationIdRef.current === null
        ? null
        : String(conversationIdRef.current);
    await handleStopActiveSubmission();
  }, [handleStopActiveSubmission]);
  const registerResponseIdentity = useCallback(
    (submissionId: string, responseConversationId: string) => {
      if (
        !freshChatConversationGuardRef.current.acceptResponseIdentity(
          responseConversationId,
        )
      ) {
        return;
      }
      const parsedConversationId = Number(responseConversationId);
      if (Number.isSafeInteger(parsedConversationId)) {
        setActiveConversationId(parsedConversationId);
      }
      registerSubmissionId(submissionId, responseConversationId);
    },
    [
      registerSubmissionId,
      setActiveConversationId,
      freshChatConversationGuardRef,
    ],
  );

  useEffect(() => {
    submissionIdListenerRef.current = registerResponseIdentity;
    return () => {
      if (submissionIdListenerRef.current === registerResponseIdentity) {
        submissionIdListenerRef.current = null;
      }
    };
  }, [registerResponseIdentity, submissionIdListenerRef]);

  useEffect(() => {
    if (!isLoading) {
      finishCancellationStream();
    }
  }, [finishCancellationStream, isLoading]);

  // File upload hook shares terminal/replay identity handling with the
  // package-managed chat stream.
  const fileUploadApiBaseUrl = `${getApiBaseUrl()}/api/workspace/ida`;
  const fileUploadResumeOptions = buildChatResumeOptions(fileUploadApiBaseUrl);
  const { sendWithFiles, isUploading, cancelLocalStream } =
    useSendMessageWithFiles({
      apiBaseUrl: fileUploadApiBaseUrl,
      authHeaders,
      visibleMessages,
      visibleMessagesConversationId,
      getAuthHeaders: getFreshAuthHeaders,
      currentUserId: currentUser?.id,
      onSubmissionId: registerResponseIdentity,
      onTerminalAccepted: fileUploadResumeOptions.onTerminalAccepted,
      onReplayUnavailable: fileUploadResumeOptions.onReplayUnavailable,
    });
  useEffect(() => {
    cancelFileStreamRef.current = cancelLocalStream;
    return () => {
      cancelFileStreamRef.current = () => {};
    };
  }, [cancelLocalStream]);

  // Fetch UI config for workspace personalization
  const fetchUIConfig = useCallback(async () => {
    try {
      const config = await authFetch(
        `/api/workspace/ida/context/ui-config`,
        "fetch UI config",
        { headers: { "X-Workspace-Id": workspaceId } },
      );
      setUiConfig(config);
    } catch (err) {
      // Non-critical - just use defaults
      console.warn("Failed to fetch UI config:", err);
    } finally {
      setUiConfigLoaded(true);
    }
  }, [authFetch, workspaceId]);

  // Fetch UI config on mount
  useEffect(() => {
    fetchUIConfig();
  }, [fetchUIConfig, workspaceId]);

  useEffect(() => {
    if (!skillAttributionEnabled || !conversationId) {
      setSkillAttributionsByTurnId({});
      return;
    }

    const numericConversationId =
      typeof conversationId === "number"
        ? conversationId
        : parseInt(conversationId as string, 10);
    if (isNaN(numericConversationId)) {
      setSkillAttributionsByTurnId({});
      return;
    }

    let active = true;
    const loadAttributions = async () => {
      try {
        const response = (await authFetch(
          `/api/workspace/ida/chat/conversations/${numericConversationId}/skill-attributions`,
          "fetch conversation skill attributions",
          { headers: { "X-Workspace-Id": workspaceId } },
        )) as SkillInvocationAttributionResponse;
        if (!active) return;

        const grouped = response.items.reduce<
          Record<number, SkillInvocationAttributionResponse["items"]>
        >((acc, item) => {
          if (item.turn_id == null) return acc;
          acc[item.turn_id] = [...(acc[item.turn_id] || []), item];
          return acc;
        }, {});
        setSkillAttributionsByTurnId(grouped);
      } catch (err) {
        console.warn("Failed to fetch conversation skill attributions:", err);
        if (active) {
          setSkillAttributionsByTurnId({});
        }
      }
    };

    loadAttributions();
    return () => {
      active = false;
    };
  }, [
    authFetch,
    attributionTurnSignature,
    conversationId,
    skillAttributionEnabled,
    workspaceId,
  ]);

  // Watch for completed background jobs that request context/workspace refresh.
  // Extracted to useJobCompletionRefresh for unit-testability -- including the
  // Task #47 onboarding-config refresh fired on workspace-settings persistence.
  // Unlock the WorkspaceSidebar nav. The sidebar gates on workspace data
  // (durable, via refreshWorkspace) OR an instant same-tab `sessionIsReady`
  // flag flipped by the `mm-ida-workspace-ready` event. Fire both: the event
  // unlocks immediately, the refetch makes it survive reload/other tabs.
  const signalWorkspaceReady = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent("mm-ida-workspace-ready", {
        detail: { workspaceId },
      }),
    );
    refreshWorkspace();
  }, [refreshWorkspace, workspaceId]);

  const handleWorkspaceSettingsRefresh = useCallback(() => {
    onboardingInProgressRef.current = false;
    signalWorkspaceReady();
  }, [signalWorkspaceReady]);
  useJobCompletionRefresh(backgroundJobs, {
    onRefreshUIConfig: fetchUIConfig,
    onRefreshWorkspace: handleWorkspaceSettingsRefresh,
    onRefreshOnboardingConfig: refreshOnboardingConfig,
  });

  // Task #51 (#1167-v2): guaranteed onboarding-header clearing. The
  // refresh_workspace job signal above only fires if the agent calls
  // update_workspace_settings; this refetches onboarding config whenever an
  // assistant turn settles while still onboarding, so the stale X-Onboarding-
  // Mode header drops regardless of which tools the agent invoked.
  useOnboardingExitRefresh(isLoading, needsOnboarding, refreshOnboardingConfig);

  // Guaranteed in-session sidebar unlock. The refresh_workspace job signal is
  // unreliable (it only fires if the agent surfaces update_workspace_settings
  // as a completed background job), so the sidebar previously stayed locked
  // until the window refocused and WorkspaceProvider's focus-refetch ran.
  // useOnboardingExitRefresh's guaranteed refreshOnboardingConfig flips
  // needsOnboarding true -> false the moment the BE clears it; ride that
  // transition to refresh workspace data and dispatch the instant-unlock event.
  // The transition only happens on genuine completion (is_admin is stable, so
  // the only driver is needs_onboarding clearing server-side), so this can't
  // unlock prematurely mid-onboarding.
  useEffect(() => {
    const wasOnboarding = prevUiNeedsOnboardingRef.current;
    prevUiNeedsOnboardingRef.current = needsOnboarding;
    if (wasOnboarding && !needsOnboarding) {
      signalWorkspaceReady();
    }
  }, [needsOnboarding, signalWorkspaceReady]);

  // A durable onboarding-agent marker prevents a revisit from sending the
  // hidden setup prompt again while workspace settings remain blank.
  useWorkspaceOnboardingAutoTrigger({
    needsOnboarding: Boolean(needsOnboarding),
    onboardingAgentExists,
    hasMessages: messages.length > 0,
    hasConversation: conversationId !== null,
    hasAnyConversation: conversations.length > 0,
    conversationInventoryLoaded:
      conversationInventoryWorkspaceId === workspaceId,
    setupComplete: Boolean(
      workspace?.description?.trim() ||
      workspace?.assistant_instructions?.trim(),
    ),
    isLoading,
    isInProgress: () => onboardingInProgressRef.current,
    onStarted: () => {
      onboardingInProgressRef.current = true;
    },
    sendOnboarding: () =>
      handleSendMessage(
        WORKSPACE_ONBOARDING_TRIGGER_MESSAGE,
        undefined,
        undefined,
        "workspace_onboarding_trigger",
      ),
    onError: (error) => {
      console.error("[IDA Onboarding] sendMessage failed:", error);
    },
    onFailed: () => {
      onboardingInProgressRef.current = false;
    },
  });

  // Handle citation click - open document preview
  const handleCitationClick = useCallback((citation: Citation) => {
    setSelectedCitation(citation);
    setPreviewOpen(true);
  }, []);

  // Handle new conversation
  const handleNewConversation = useCallback(() => {
    if (needsOnboarding && workspaceMode === "chat") {
      setOnboardingChatAutoSelectionSuppressedWorkspaceId(workspaceId);
    }
    if (workspaceMode === "agent") {
      if (
        !scheduleAgentCreation(
          () => handleSendMessage(AGENT_ONBOARDING_TRIGGER_MESSAGE),
          (error) => {
            console.error("[IDA Agent Mode] onboarding send failed:", error);
          },
        )
      ) {
        return;
      }
      setNewAgentSelectionPending(true);
    }
    // Task #188: cancel any in-flight stream before resetting. When a prior
    // conversation ended in a failed/loading state (e.g. SSE drop on a long
    // PPTX run), the library's isLoading flag and stream reconnect attempts
    // remain active. The next sendMessage on a fresh conv then short-circuits
    // (sendMessage early-returns when isLoading=true) or piggybacks on the
    // stuck stream, contaminating the new conversation with prior state.
    // Cancel even while idle: the chat UI package can keep a passive
    // conversation subscription open when isLoading is false.
    resetChatProvider(
      useChatStore.getState().conversationId,
      cancelStream,
      () => {
        if (isLoading) setLoading(false);
      },
    );
    startNewConversation();
    resetConversationMessageWindow();
    messagesRef.current = [];
    clearMessages();
    setActiveConversationId(null);
    setCurrentConversationCollaborators([]);
    setConversationCollaboratorsError(null);
    setSavedConversationId(null);
    sessionStorage.removeItem(sessionStorageKey);
    dispatchHistoryLoadEvent({
      type: "newConversation",
      generation: messageLoadGenerationRef.current,
    });
    void refreshConversations({ search: conversationSearch, skip: 0 });
  }, [
    isLoading,
    cancelStream,
    setLoading,
    startNewConversation,
    resetConversationMessageWindow,
    clearMessages,
    setActiveConversationId,
    resetChatProvider,
    sessionStorageKey,
    refreshConversations,
    conversationSearch,
    handleSendMessage,
    workspaceMode,
    needsOnboarding,
    workspaceId,
    scheduleAgentCreation,
    setNewAgentSelectionPending,
  ]);

  const handleLoadConversation = useCallback(
    (id: number) => {
      if (needsOnboarding && workspaceMode === "chat") {
        setOnboardingChatAutoSelectionSuppressedWorkspaceId(workspaceId);
      }
      return loadConversation(id);
    },
    [loadConversation, needsOnboarding, workspaceId, workspaceMode],
  );

  // Handle conversation rename with optimistic local update + rollback on
  // failure. FE-side trims and rejects empty input before round-tripping;
  // the BFF also enforces non-empty (min_length=1, max_length=255), and
  // the hub adjudicates owner-or-member auth.
  const handleRenameConversation = useCallback(
    async (id: number, nextTitle: string): Promise<void> => {
      const trimmed = nextTitle.trim();
      if (!trimmed) return;
      let previousTitle: string | null = null;
      setConversations((current) =>
        current.map((conversation) => {
          if (conversation.id !== id) return conversation;
          previousTitle = conversation.title;
          return { ...conversation, title: trimmed };
        }),
      );
      try {
        await renameChatConversation(workspaceId, id, trimmed);
      } catch (error) {
        // Roll back optimistic update on any failure.
        setConversations((current) =>
          current.map((conversation) =>
            conversation.id === id
              ? { ...conversation, title: previousTitle }
              : conversation,
          ),
        );
        throw error;
      }
    },
    [renameChatConversation, workspaceId],
  );

  const handleUpdateAgentConversation = useCallback(
    async (
      id: number,
      patch: {
        agentName?: string;
        systemPromptOverride?: string | null;
        personaSourceId?: string;
      },
    ): Promise<void> => {
      let previous: Pick<
        CollaborationConversationSummary,
        "agentName" | "systemPromptOverride"
      > | null = null;
      setConversations((current) =>
        current.map((conversation) => {
          if (conversation.id !== id) return conversation;
          previous = {
            agentName: conversation.agentName,
            systemPromptOverride: conversation.systemPromptOverride,
          };
          return {
            ...conversation,
            agentName: patch.agentName ?? conversation.agentName,
            systemPromptOverride: patch.personaSourceId
              ? conversation.systemPromptOverride
              : (patch.systemPromptOverride ??
                conversation.systemPromptOverride),
          };
        }),
      );
      try {
        const updated = await updateAgentConversation(workspaceId, id, {
          agent_name: patch.agentName,
          ...(patch.personaSourceId
            ? { persona_source_id: patch.personaSourceId }
            : { system_prompt_override: patch.systemPromptOverride }),
        });
        if (updated) {
          setConversations((current) =>
            current.map((conversation) =>
              conversation.id === id
                ? mergeAgentPersonaPatchResponse(conversation, updated)
                : conversation,
            ),
          );
        }
      } catch (error) {
        if (previous) {
          setConversations((current) =>
            current.map((conversation) =>
              conversation.id === id
                ? {
                    ...conversation,
                    agentName: previous?.agentName ?? null,
                    systemPromptOverride:
                      previous?.systemPromptOverride ?? null,
                  }
                : conversation,
            ),
          );
        }
        throw error;
      }
    },
    [updateAgentConversation, workspaceId],
  );

  const handleAssignAgentAvatar = useCallback(
    async (
      id: number,
      selection: { selected_cut: string; selected_colour: string },
    ) => {
      try {
        await assignAgentAvatar(workspaceId, id, selection);
      } finally {
        // Allocation conflicts and successful writes both require the
        // authoritative list overlay; never retain an optimistic pair.
        // refreshConversations owns and absorbs its fetch errors, so its
        // refresh cannot replace this PUT's success or HTTP status.
        await refreshConversations({ search: conversationSearch, skip: 0 });
      }
    },
    [assignAgentAvatar, conversationSearch, refreshConversations, workspaceId],
  );

  const handleGetAgentAvatarReservations = useCallback(
    (id: number) => getAgentAvatarReservations(workspaceId, id),
    [getAgentAvatarReservations, workspaceId],
  );

  const handleReleaseAgentAvatar = useCallback(
    async (id: number) => {
      try {
        await releaseAgentAvatar(workspaceId, id);
      } finally {
        await refreshConversations({ search: conversationSearch, skip: 0 });
      }
    },
    [conversationSearch, refreshConversations, releaseAgentAvatar, workspaceId],
  );

  // Handle delete conversation - clear chat if deleting active conversation
  const handleDeleteConversation = useCallback(
    async (id: number) => {
      try {
        await deleteChatConversation(workspaceId, id);
      } catch (error) {
        // Agent deletion is local-identity-first. A Hub failure leaves the
        // conversation visible and recoverable, so reload the API-owned
        // avatar overlay rather than treating the local identity release as
        // a successful UI deletion.
        await recoverAgentDeleteFailure(error, async () => {
          await refreshConversations({ search: conversationSearch, skip: 0 });
        });
      }
      setConversations((current) =>
        current.filter((conversation) => conversation.id !== id),
      );
      setConversationCollaboratorsById((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
      if (id === conversationId || String(id) === String(conversationId)) {
        resetConversationMessageWindow();
        messagesRef.current = [];
        clearMessages();
        setActiveConversationId(null);
        setCurrentConversationCollaborators([]);
        sessionStorage.removeItem(sessionStorageKey);
      }
      // Fired unconditionally, not only for the active conversation: a
      // failed load for a conversation the user has since navigated away
      // from is still cleared when that conversation is deleted, rather
      // than leaving a load-failed id on record for a conversation that no
      // longer exists and can never become active again.
      dispatchHistoryLoadEvent({
        type: "conversationDeleted",
        conversationId: id,
        generation: messageLoadGenerationRef.current,
      });
    },
    [
      clearMessages,
      conversationId,
      conversationSearch,
      deleteChatConversation,
      refreshConversations,
      resetConversationMessageWindow,
      sessionStorageKey,
      setActiveConversationId,
      workspaceId,
    ],
  );

  // Handle help button click - send help request message
  const handleHelpClick = useCallback(() => {
    handleSendMessage(
      "Help me understand what I can do here and what this workspace contains.",
    );
  }, [handleSendMessage]);

  // Handle sending message with files
  const handleSendWithFiles = useCallback(
    async (content: string, files: File[], images?: ImageReference[]) => {
      freshChatConversationGuardRef.current.prepareSend(
        useChatStore.getState().conversationId,
        () => useChatStore.getState().setConversationId(null),
      );
      setUserStoppedConversationId(null);
      submittedAssistantMessageIdRef.current = null;
      await sendWithFiles(content, files, images);
    },
    [sendWithFiles, freshChatConversationGuardRef],
  );

  const handleRestoreMessageFiles = useCallback(
    async (message: ChatMessageWithFiles): Promise<File[]> => {
      const attachments = message.fileAttachments || [];
      if (attachments.length === 0) {
        return [];
      }

      return Promise.all(
        attachments.map(async (attachment) => {
          if (!attachment.docId) {
            throw new Error(
              `Attachment ${attachment.filename} is missing its document id`,
            );
          }

          const blob = (await downloadDocument(
            workspaceId,
            attachment.docId,
            attachment.filename,
            { responseType: "blob" },
          )) as Blob;

          return new File([blob], attachment.filename, {
            type:
              attachment.mimeType || blob.type || "application/octet-stream",
          });
        }),
      );
    },
    [downloadDocument, workspaceId],
  );
  const handleSearchConversations = useCallback(
    (query: string) => {
      void refreshConversations({ search: query, skip: 0 });
    },
    [refreshConversations],
  );

  const handleLoadMoreConversations = useCallback(() => {
    if (
      conversations.length >= conversationTotal ||
      conversationsLoading ||
      loadMoreConversationsInFlightRef.current
    ) {
      return;
    }
    loadMoreConversationsInFlightRef.current = true;
    return refreshConversations({
      search: conversationSearch,
      skip: conversations.length,
    }).finally(() => {
      loadMoreConversationsInFlightRef.current = false;
    });
  }, [
    conversationSearch,
    conversationTotal,
    conversations.length,
    conversationsLoading,
    refreshConversations,
  ]);

  const handleAddCollaborators = useCallback(
    async (userIds: string[]) => {
      const activeConversationId =
        typeof conversationId === "number"
          ? conversationId
          : typeof conversationId === "string"
            ? Number.parseInt(conversationId, 10)
            : NaN;
      if (!Number.isFinite(activeConversationId)) return;

      setConversationCollaboratorsError(null);
      try {
        const collaborators = await addConversationCollaborators(
          workspaceId,
          activeConversationId,
          userIds,
        );
        setCurrentConversationCollaborators(collaborators);
        setConversationCollaboratorsById((current) => ({
          ...current,
          [activeConversationId]: collaborators,
        }));
        void refreshConversations({ search: conversationSearch, skip: 0 });
      } catch (err: any) {
        const message = err?.message || "Failed to add collaborators.";
        setConversationCollaboratorsError(message);
        throw err;
      }
    },
    [
      addConversationCollaborators,
      conversationId,
      conversationSearch,
      refreshConversations,
      workspaceId,
    ],
  );

  const handleRemoveCollaborator = useCallback(
    async (userId: string) => {
      const activeConversationId =
        typeof conversationId === "number"
          ? conversationId
          : typeof conversationId === "string"
            ? Number.parseInt(conversationId, 10)
            : NaN;
      if (!Number.isFinite(activeConversationId)) return;

      setConversationCollaboratorsError(null);
      try {
        await removeConversationCollaborator(
          workspaceId,
          activeConversationId,
          userId,
        );
        const nextCollaborators = currentConversationCollaborators.filter(
          (collaborator) => collaborator.user_id !== userId,
        );
        setCurrentConversationCollaborators(nextCollaborators);
        setConversationCollaboratorsById((current) => ({
          ...current,
          [activeConversationId]: nextCollaborators,
        }));
        void refreshConversations({ search: conversationSearch, skip: 0 });
      } catch (err: any) {
        const message = err?.message || "Failed to remove collaborator.";
        setConversationCollaboratorsError(message);
        throw err;
      }
    },
    [
      conversationId,
      conversationSearch,
      currentConversationCollaborators,
      refreshConversations,
      removeConversationCollaborator,
      workspaceId,
    ],
  );

  // Handle delete messages from a point - used for both edit and delete
  const handleDeleteMessagesFrom = useCallback(
    async (
      message: Pick<IDAChatMessage, "id" | "hubMessageId" | "role" | "content">,
    ) => {
      setDeleteMessageError(null);

      const reportDeleteFailure = (name: string, message: string) => {
        setDeleteMessageError(
          "Could not delete this message. Please try again.",
        );
        const error = new Error(message);
        error.name = name;
        reportFrontendError(error, "", "window_error");
      };

      if (!conversationId) {
        reportDeleteFailure(
          "chat_delete_unresolved_id",
          "No active conversation is available for message deletion.",
        );
        return;
      }

      const activeConversationId =
        typeof conversationId === "number"
          ? conversationId
          : parseInt(conversationId, 10);
      if (!Number.isFinite(activeConversationId)) {
        reportDeleteFailure(
          "chat_delete_unresolved_id",
          "The active conversation ID could not be resolved.",
        );
        return;
      }
      const deleteGeneration = messageLoadGenerationRef.current + 1;
      messageLoadGenerationRef.current = deleteGeneration;
      olderMessageRequestRef.current = null;
      notifyOlderMessageRequestComplete();
      setOlderMessagesLoading(false);
      deleteMessageInFlightRef.current = true;

      let hubId = resolveDeleteHubMessageId(message, []);

      if (hubId === null && !isNaN(activeConversationId)) {
        try {
          let searchWindow =
            messageWindowConversationIdRef.current === activeConversationId
              ? messageWindowRef.current
              : null;
          if (!searchWindow) {
            searchWindow = replaceConversationMessageWindow(
              await fetchConversationMessagePage(activeConversationId),
            );
          }

          hubId = resolveDeleteHubMessageId(
            message,
            mapConversationMessageWindow(searchWindow),
          );
          const visitedCursors = new Set<number>();
          let pagesFetched = 0;
          while (
            hubId === null &&
            searchWindow.hasMore &&
            searchWindow.nextBeforeId !== null &&
            pagesFetched < MAX_MESSAGE_GAP_RECONNECT_PAGES &&
            !visitedCursors.has(searchWindow.nextBeforeId)
          ) {
            const beforeId = searchWindow.nextBeforeId;
            visitedCursors.add(beforeId);
            const olderPage = await fetchConversationMessagePage(
              activeConversationId,
              beforeId,
            );
            searchWindow = prependOlderConversationMessagePage(
              searchWindow,
              olderPage,
            );
            pagesFetched += 1;
            hubId = resolveDeleteHubMessageId(
              message,
              mapConversationMessageWindow(searchWindow),
            );
          }
        } catch (err) {
          console.warn(
            "[IDA Chat] Failed to resolve hub id for delete fallback:",
            err,
          );
        }
      }

      if (hubId === null) {
        // Keep the visible row intact when it cannot be mapped to a persisted
        // Hub message. The user can retry after history has synced.
        reportDeleteFailure(
          "chat_delete_unresolved_id",
          "The persisted message ID could not be resolved.",
        );
        deleteMessageInFlightRef.current = false;
        return;
      }

      try {
        const response = await fetch(
          `${getApiBaseUrl()}/api/workspace/ida/chat/conversations/${conversationId}/messages/${hubId}`,
          {
            method: "DELETE",
            headers: authHeaders,
          },
        );
        if (!response.ok) {
          reportDeleteFailure(
            "chat_delete_request_failed",
            `The message DELETE returned HTTP ${response.status}.`,
          );
          return;
        }

        // Update visible state only after the server confirms the deletion.
        if (
          String(conversationIdRef.current) === String(activeConversationId)
        ) {
          deleteLocalMessagesFrom(message.id);
        }

        // Prune the retained raw window immediately so a delayed visible-state
        // update cannot resurrect the deleted suffix. Then reconcile only the
        // newest page, preserving any older pages the user explicitly loaded.
        if (!isNaN(activeConversationId)) {
          let currentWindow =
            messageWindowConversationIdRef.current === activeConversationId
              ? messageWindowRef.current
              : null;
          if (currentWindow) {
            currentWindow = removeConversationMessagesFrom(
              currentWindow,
              hubId,
            );
            publishConversationMessageWindow(
              activeConversationId,
              currentWindow,
              { replaceVisible: true },
            );
          }

          try {
            const newestPage =
              await fetchConversationMessagePage(activeConversationId);
            if (
              messageLoadGenerationRef.current !== deleteGeneration ||
              String(conversationIdRef.current) !== String(activeConversationId)
            ) {
              return;
            }
            const latestWindow =
              messageWindowConversationIdRef.current === activeConversationId
                ? messageWindowRef.current
                : null;
            const reconciled = latestWindow
              ? mergeNewestConversationMessagePage(latestWindow, newestPage)
              : replaceConversationMessageWindow(newestPage);
            publishConversationMessageWindow(activeConversationId, reconciled, {
              replaceVisible: true,
            });
          } catch (err) {
            console.warn(
              "[IDA Chat] Post-delete message reconciliation failed:",
              err,
            );
          }
        }
      } catch (err) {
        console.error("Failed to delete message from Hub.");
        reportDeleteFailure(
          "chat_delete_request_failed",
          "The message DELETE request failed before completion.",
        );
      } finally {
        deleteMessageInFlightRef.current = false;
      }
    },
    [
      conversationId,
      authHeaders,
      fetchConversationMessagePage,
      publishConversationMessageWindow,
      deleteLocalMessagesFrom,
      notifyOlderMessageRequestComplete,
      reportFrontendError,
    ],
  );

  // Fetch usage data for a message (lazy loading on hover)
  const fetchMessageUsage = useCallback(
    async (conversationId: number, messageId: number) => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/api/workspace/ida/chat/conversations/${conversationId}/messages/${messageId}/usage`,
          { headers: authHeaders },
        );
        if (!response.ok) {
          if (response.status === 404) return null;
          throw new Error(`Failed to fetch usage: ${response.status}`);
        }
        return await response.json();
      } catch (err) {
        console.error("Failed to fetch message usage:", err);
        return null;
      }
    },
    [authHeaders],
  );

  // Determine welcome description with fallback for empty workspaces
  // Show onboarding-specific messages when setting up workspace
  const welcomeDescription = !uiConfigLoaded
    ? undefined
    : needsOnboarding
      ? "I'll ask a few quick questions to configure this workspace for your needs."
      : uiConfig?.welcome_description ||
        (uiConfig?.has_documents === false
          ? "Upload documents to get started, or ask me how I can help."
          : "Ask me anything about your documents.");

  const welcomeTitle = !uiConfigLoaded
    ? undefined
    : needsOnboarding
      ? `Let's set up ${workspaceName}`
      : `Welcome to ${workspaceName}`;

  const inputPlaceholder = !uiConfigLoaded
    ? undefined
    : uiConfig?.input_placeholder || "Ask me anything about your documents...";

  const iconSuggestions = !uiConfigLoaded
    ? []
    : uiConfig?.icon_suggestions || [];

  // Show while the workspace needs onboarding and the backend hasn't cleared it yet.
  // Hides as soon as uiConfig.needs_onboarding transitions to false (same session).
  const showOnboardingHint =
    needsOnboarding && uiConfig?.needs_onboarding !== false;
  useTutorialHint(
    {
      id: "ida-onboarding-chat-setup",
      targetId: "ida-onboarding-composer",
      title: "Set up this workspace",
      body: "Complete the setup conversation to configure your workspace. Once done, the sidebar options will unlock automatically.",
      placement: "top",
      noPersist: true,
    },
    showOnboardingHint,
  );

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {stopError && <Alert color="danger">{stopError}</Alert>}
      {deleteMessageError && (
        <Alert
          color="danger"
          variant="soft"
          role="alert"
          endDecorator={
            <IconButton
              aria-label="Dismiss delete message error"
              variant="plain"
              size="sm"
              color="danger"
              onClick={() => setDeleteMessageError(null)}
            >
              <CloseIcon />
            </IconButton>
          }
        >
          {deleteMessageError}
        </Alert>
      )}
      {activeJobsStatusUnavailable && (
        <Alert color="warning">Couldn't load background job status</Alert>
      )}
      {streamRecovery.state === "checking" && (
        <Alert color="neutral">Checking conversation updates…</Alert>
      )}
      {streamRecovery.state === "reconnecting" && (
        <Alert color="warning">
          Updates are delayed. The assistant is still working; checking again
          shortly.
        </Alert>
      )}
      {streamRecovery.state === "error" && (
        <Alert color="danger">
          Could not check the latest conversation updates.{" "}
          <Button
            size="sm"
            variant="outlined"
            onClick={() => void streamRecovery.retry()}
          >
            Retry
          </Button>
        </Alert>
      )}
      <FullPageChatLayout
        messages={visibleMessages}
        toolComposingLabel={toolComposingLabel}
        visibleMessagesConversationId={visibleMessagesConversationId}
        hasOlderMessages={hasOlderMessages}
        onLoadOlderMessages={handleLoadOlderMessages}
        olderMessagesLoading={olderMessagesLoading}
        olderMessagesLoadFailed={olderMessagesLoadFailed}
        onSendMessage={handleSendMessage}
        onTextSendStarted={(draft, restore) => {
          pendingComposerDraftRef.current = {
            draft,
            restore,
            submissionId: null,
            streamStarted: false,
            sending: true,
            restored: false,
            assistantMessageId: null,
          };
        }}
        onQueueMessage={conversationQueue.enqueue}
        queuedMessages={conversationQueue.items}
        queueError={conversationQueue.error}
        onCancelQueuedMessage={conversationQueue.cancelItem}
        onCancelAllQueuedMessages={conversationQueue.cancelAll}
        imageAttachmentsEnabled={imageAttachments.enabled}
        uploadImage={(file) =>
          imageAttachments.uploadImage(
            file,
            useChatStore.getState().conversationId,
          )
        }
        isLoading={isLoading}
        isStopping={isStopping}
        conversationSwitching={historyLoadState.loading}
        agentCreationPending={agentCreationPending}
        conversations={conversations}
        onLoadConversation={handleLoadConversation}
        onNewConversation={handleNewConversation}
        onSearchConversations={handleSearchConversations}
        onLoadMoreConversations={handleLoadMoreConversations}
        hasMoreConversations={conversations.length < conversationTotal}
        onDeleteConversation={handleDeleteConversation}
        onRenameConversation={handleRenameConversation}
        onUpdateAgentConversation={handleUpdateAgentConversation}
        onAssignAgentAvatar={handleAssignAgentAvatar}
        onGetAgentAvatarReservations={handleGetAgentAvatarReservations}
        onReleaseAgentAvatar={handleReleaseAgentAvatar}
        conversationsLoading={conversationsLoading}
        currentConversationId={conversationId}
        onCitationClick={handleCitationClick}
        fetchMessageUsage={fetchMessageUsage}
        title={workspaceName}
        welcomeTitle={welcomeTitle}
        welcomeDescription={welcomeDescription}
        historyLoadFailed={
          historyLoadState.loadFailedConversationId !== null &&
          String(historyLoadState.loadFailedConversationId) ===
            String(conversationId)
        }
        historyLoading={historyLoadState.loading}
        onRetryHistoryLoad={handleRetryHistoryLoad}
        messagesSyncError={messagesSyncError}
        onDismissMessagesSyncError={() => setMessagesSyncError(null)}
        inputPlaceholder={inputPlaceholder}
        iconSuggestions={iconSuggestions}
        onHelpClick={handleHelpClick}
        onSendMessageWithFiles={handleSendWithFiles}
        onRestoreMessageFiles={handleRestoreMessageFiles}
        isUploading={isUploading}
        onDeleteMessagesFrom={handleDeleteMessagesFrom}
        onCancelStream={handleStopSubmission}
        chatDisabled={periodicExceeded}
        stickyTopContent={
          <>
            <HeartbeatPanel workspaceId={workspaceId} isAdmin={isAdmin} />
          </>
        }
        skillAttributionEnabled={skillAttributionEnabled}
        followUpSuggestions={suggestionChips}
        skillAttributionsByTurnId={skillAttributionsByTurnId}
        collaborationEnabled={collaborationEnabled}
        currentUser={currentUser}
        workspaceUsers={workspaceUsers}
        currentConversationCollaborators={currentConversationCollaborators}
        conversationCollaboratorsById={conversationCollaboratorsById}
        conversationCollaboratorsLoading={conversationCollaboratorsLoading}
        conversationCollaboratorsError={conversationCollaboratorsError}
        canManageCollaborators={
          currentConversationCollaborators.some(
            (collaborator) =>
              collaborator.user_id === currentUser?.id &&
              collaborator.role === "owner",
          ) ||
          // Task #211: BE inserts owner collaborator record async after conv
          // creation. First fetch on a brand-new conv often returns [] before
          // the owner record lands. Reload-recovery proves race shape. Without
          // this fallback, the Collaborate button stays hidden on fresh chats
          // until the user reloads. When the list is empty, not loading, and
          // there's no error, the user is the implicit owner (they just
          // created the conv).
          (!conversationCollaboratorsLoading &&
            !conversationCollaboratorsError &&
            currentConversationCollaborators.length === 0)
        }
        onAddCollaborators={handleAddCollaborators}
        onRemoveCollaborator={handleRemoveCollaborator}
        voiceInputEnabled={chatVoiceFeatures.dictationEnabled}
        gptLiveVoiceControl={
          chatVoiceFeatures.control === "gpt-live" &&
          (conversationId === null ||
            (Number.isSafeInteger(Number(conversationId)) &&
              Number(conversationId) > 0)) ? (
            <GptLiveVoiceControl
              workspaceId={workspaceId}
              conversationId={
                conversationId === null ? null : Number(conversationId)
              }
              ensureConversation={ensureGptLiveConversation}
              authFetch={authFetch}
              onPhaseChange={setGptLiveVoicePhase}
              onToolComposing={handleVoiceToolComposing}
              onTranscriptTurns={handleGptLiveTranscriptTurns}
              onFeedbackChange={setGptLiveVoiceFeedback}
            />
          ) : null
        }
        gptLiveVoiceFeedback={
          chatVoiceFeatures.control === "gpt-live" ? gptLiveVoiceFeedback : null
        }
        workspaceId={workspaceId}
        workspaceMode={workspaceMode}
        needsOnboarding={needsOnboarding}
        onboardingAgentId={onboardingAgentId}
        workspaceRole={workspace?.permissions.role ?? null}
        agentStatusPushActive={agentStatusPushActive}
      />

      {/* Document preview drawer */}
      <DocumentPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        workspaceId={workspaceId}
        citation={selectedCitation}
      />

      {/* Tutorial anchor — transparent, non-interactive, fixed at the composer position */}
      <Box
        ref={composerAnchorRef}
        aria-hidden="true"
        sx={{
          position: "fixed",
          bottom: 0,
          left: "var(--workspace-sidebar-width, 0px)",
          right: 0,
          height: "88px",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
};

export default Chat;
